import Phaser from 'phaser';
import { defaultGameConfig, GameConfig, TimingResult, HitResult } from '../config/gameConfig';
import { callGameEnd } from '../utils/gamebox';

interface PitchResult {
  result: HitResult;
  distance: number;
  score: number;
  timingResult?: TimingResult; // スイングタイミング結果
  angle?: number; // 角度
}

// Lottieアニメーション用の型定義
declare global {
  interface Window {
    lottie?: {
      loadAnimation: (params: {
        container: HTMLElement;
        renderer: 'svg' | 'canvas' | 'html';
        loop?: boolean | number;
        autoplay?: boolean;
        path?: string;
        animationData?: any;
      }) => {
        play: () => void;
        stop: () => void;
        pause: () => void;
        destroy: () => void;
        setSpeed: (speed: number) => void;
        setDirection: (direction: number) => void;
        goToAndStop: (value: number, isFrame?: boolean) => void;
        goToAndPlay: (value: number, isFrame?: boolean) => void;
      };
    };
  }
}

export class GameScene extends Phaser.Scene {
  private config!: GameConfig;
  private currentPitch: number = 0;
  private totalScore: number = 0;
  private pitchResults: PitchResult[] = [];
  private strikes: number = 0; // ストライクカウント（0-3）
  private currentPitchSpeed: number = 500; // 現在の投球速度（ピクセル/秒）
  private currentPitchType: 'ultraFast' | 'fast' | 'slow' | 'knuckleball1' | 'knuckleball2' | 'knuckleball3' | 'knuckleball4' | 'knuckleball5' | 'knuckleball6' = 'fast'; // 現在の投球タイプ
  private ballTween: Phaser.Tweens.Tween | null = null; // ボールのtween（魔球用に保存）
  private ballClones: Phaser.GameObjects.Image[] = []; // 分身ボール（魔球4用）
  private particleAnimation: any = null; // Lottieアニメーションインスタンス
  private particleContainer: HTMLElement | null = null; // パーティクルアニメーションのコンテナ
  private fenceHitSoundPlayed: boolean = false; // フェンス衝突音が再生済みかどうか

  // ゲームオブジェクト
  private ball!: Phaser.GameObjects.Image | null;
  private pitcher!: Phaser.GameObjects.Sprite; // ピッチャー（スプライトアニメーション対応）
  private batter!: Phaser.GameObjects.Sprite; // バッター（スプライトアニメーション対応）
  private catcher!: Phaser.GameObjects.Image; // キャッチャー
  private bat: Phaser.GameObjects.Rectangle | null = null; // バット（削除済み）
  private ground!: Phaser.GameObjects.TileSprite; // 地面
  private groundWidth!: number; // 地面の幅（動的に拡張）
  private bg!: Phaser.GameObjects.TileSprite; // 背景
  private angleBarBg!: Phaser.GameObjects.Image | null; // 角度バーの背景（画像）
  private angleBarCursor!: Phaser.GameObjects.Arc | null; // 角度バーのカーソル（削除済み）
  private bestBandArc!: Phaser.GameObjects.Graphics | null; // Best帯（画像に含まれているため非表示）
  private hitBall!: Phaser.GameObjects.Image | null; // 打球（飛んでいくボール）
  private hitBallBody!: Phaser.Physics.Matter.Sprite | null; // 打球の物理ボディ
  private groundBody!: MatterJS.BodyType | null; // 地面の物理ボディ（静的）
  private fenceBody!: MatterJS.BodyType | null; // フェンスの物理ボディ（静的）
  private fenceVisual!: Phaser.GameObjects.Image | null; // フェンスの見た目
  private distanceMarkers: Phaser.GameObjects.Group | null = null; // 距離メモリのグループ
  private ballLanded: boolean = false; // ボールが着地したかどうか
  private ballStopped: boolean = false; // ボールが止まったかどうか
  private ballStoppedFrameCount: number = 0; // ボールが停止状態が続いているフレーム数
  private isBallFollowStarted: boolean = false; // カメラ追従が開始されたかどうか
  private hitCutTimer: Phaser.Time.TimerEvent | null = null; // 打球フェーズ切り替えタイマー
  private waitFollowUntilCentered: boolean = false; // 中央に来るまで待機
  private prevBallScreenX: number | null = null; // 中央跨ぎ検出用
  private prevHitBallWorldX: number | null = null; // フェンス通過検出用
  private prevHitBallWorldY: number | null = null; // フェンス通過検出用
  private followXOnly: boolean = false; // 横だけ追従モード
  private followStrength: number = 0; // 0→1で立ち上げ
  private followLerpMax: number = 0.18; // 追従速度（0.12〜0.30）
  private gameStartCamY: number = 0; // ゲーム開始時のカメラの縦位置
  private swingBallScreenX: number = 0; // スイング開始時の投球ボール位置（画面座標X）
  private swingBallScreenY: number = 0; // スイング開始時の投球ボール位置（画面座標Y）
  private hasSwingBallPos: boolean = false; // スイング開始位置が保存されているか
  private angleMeterRadius!: number; // 角度メーターの半径
  private angleMeterOriginX!: number; // 角度メーターの原点X座標（右下の角）
  private angleMeterOriginY!: number; // 角度メーターの原点Y座標（右下の角）
  private angleMeterPointer!: Phaser.GameObjects.Line | null; // 角度メーターのポインター（針）
  private pitcherScreen?: { x: number; y: number }; // ピッチャーの画面座標（保存用）
  private batterScreen?: { x: number; y: number }; // バッターの画面座標（保存用）
  private catcherScreen?: { x: number; y: number }; // キャッチャーの画面座標（保存用）
  private batScreen?: { x: number; y: number }; // バットの画面座標（保存用）

  // 画面固定からワールド座標へ変換する関数
  private detachFromUIToWorld(
    obj: Phaser.GameObjects.GameObject & { x: number; y: number; setScrollFactor: (x: number, y?: number) => any }
  ): void {
    const cam = this.cameras.main;

    // obj.x,obj.y は「画面座標」（scrollFactor 0 のとき）なので、ワールド座標に変換
    const wp = cam.getWorldPoint(obj.x, obj.y);

    // 変換後にワールド物体として扱う
    obj.setScrollFactor(1, 1);
    obj.x = wp.x;
    obj.y = wp.y;
  }

  private cacheTimingBarPixels(): void {
    try {
      const texture = this.textures.get('timing_bar');
      const img = texture?.getSourceImage() as HTMLImageElement | undefined;
      if (!img || !img.width || !img.height) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);
      this.timingBarPixelData = ctx.getImageData(0, 0, img.width, img.height);
      this.timingBarPixelWidth = img.width;
      this.timingBarPixelHeight = img.height;

      // 緑色ゾーン（Best）の中央位置を検出
      const data = this.timingBarPixelData.data;
      const sampleY = Math.floor(img.height / 2);
      let greenStart = -1;
      let greenEnd = -1;
      for (let x = 0; x < img.width; x++) {
        const idx = (sampleY * img.width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        // 緑色判定（g成分が高く、r,b成分が低い）
        if (g > 150 && r < 150 && b < 150) {
          if (greenStart < 0) greenStart = x;
          greenEnd = x;
        }
      }
      if (greenStart >= 0 && greenEnd >= 0) {
        this.timingBarGreenCenterRatio = ((greenStart + greenEnd) / 2) / (img.width - 1);
        console.log('緑ゾーン検出:', { greenStart, greenEnd, centerRatio: this.timingBarGreenCenterRatio });
      }
    } catch (error) {
      console.warn('タイミングバー画像のピクセル取得に失敗:', error);
      this.timingBarPixelData = null;
    }
  }

  private evaluateTimingFromBarRatio(
    ratio: number
  ): { isMiss: true; missTimingResult: TimingResult } | { isMiss: false; timingResult: TimingResult } | null {
    if (!this.timingBarPixelData || this.timingBarPixelWidth <= 0 || this.timingBarPixelHeight <= 0) {
      return null;
    }
    const clampedRatio = Phaser.Math.Clamp(ratio, 0, 1);
    const sampleX = Math.round(clampedRatio * (this.timingBarPixelWidth - 1));
    const sampleY = Math.floor(this.timingBarPixelHeight / 2);
    const index = (sampleY * this.timingBarPixelWidth + sampleX) * 4;
    const data = this.timingBarPixelData.data;
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const a = data[index + 3];
    if (a < 50) return null;

    // 緑色を優先判定（g成分が高く、r,b成分が低い場合は緑）
    if (g > 100 && g > r && g > b && (g - r) > 50 && (g - b) > 50) {
      console.log('緑色検出（優先判定）:', { r, g, b, sampleX, ratio: clampedRatio });
      return { isMiss: false, timingResult: TimingResult.BEST };
    }

    const palette = [
      { name: 'red', r: 255, g: 0, b: 0 },
      { name: 'orange', r: 255, g: 136, b: 0 },
      { name: 'yellow', r: 255, g: 255, b: 0 },
      { name: 'green', r: 0, g: 255, b: 0 },
    ];
    let best = { name: 'unknown', dist: Number.POSITIVE_INFINITY };
    for (const color of palette) {
      const dr = r - color.r;
      const dg = g - color.g;
      const db = b - color.b;
      const dist = Math.sqrt(dr * dr + dg * dg + db * db);
      if (dist < best.dist) {
        best = { name: color.name, dist };
      }
    }
    console.log('色判定:', { r, g, b, sampleX, ratio: clampedRatio, detected: best.name, dist: best.dist });
    if (best.dist > 130) return null;

    // Bestゾーン（緑）の中央を基準に左右判定
    const isLeft = clampedRatio < this.timingBarGreenCenterRatio;
    if (best.name === 'green') {
      return { isMiss: false, timingResult: TimingResult.BEST };
    }
    if (best.name === 'yellow') {
      return { isMiss: false, timingResult: isLeft ? TimingResult.EARLY : TimingResult.LATE };
    }
    if (best.name === 'orange') {
      return { isMiss: false, timingResult: isLeft ? TimingResult.VERY_EARLY : TimingResult.VERY_LATE };
    }
    if (best.name === 'red') {
      return { isMiss: true, missTimingResult: isLeft ? TimingResult.VERY_EARLY : TimingResult.VERY_LATE };
    }
    return null;
  }

  // 角度を1/4円の範囲（180-90度）にマッピング
  // ゲームの角度（0度から90度）を1/4円の角度（180-90度）にマッピング
  // 180度が左（0度=水平）、90度が上（90度=垂直）
  private mapAngleToQuarterCircle(angle: number): number {
    const normalized = (angle - this.config.barMinAngle) / (this.config.barMaxAngle - this.config.barMinAngle);
    // 0度（実際の0度=水平）→ 180度（左方向）
    // 90度（実際の90度=垂直）→ 90度（上方向）
    return 180 - normalized * 90; // 180度から90度（0度→180度=左=水平、90度→90度=上=垂直）
  }
  private ballStartX!: number;
  private ballStartY!: number;
  private ballTargetX!: number;
  private ballTargetY!: number;
  private timingBarStartX!: number; // タイミングバーの左端（ボール位置）
  private timingBarEndX!: number; // タイミングバーの右端（キャッチャー位置）
  private timingBarWidth!: number; // タイミングバーの幅

  // ゲーム状態
  private gameState: 'waiting' | 'pitching' | 'timing' | 'angleBar' | 'result' = 'waiting';
  private timingStartTime: number = 0;
  private perfectTimingTime: number = 0;
  private pitchDuration: number = 0; // 投球時間（ミリ秒）を保存
  private finalTimingPointerX: number | null = null; // タイミングバーのポインターの最終位置
  private currentAngle: number = 0;
  private isSwingMissed: boolean = false; // 空振り判定フラグ（その1打席でスイングタップ無効）
  private angleBarSpeed: number = 0;
  private angleBarTime: number = 0;
  private tapCount: number = 0; // タップ回数をカウント
  private updateCallCount: number = 0; // デバッグ用
  private isCursorMoving: boolean = false; // カーソルが動いているか

  // UI要素
  private scoreText!: Phaser.GameObjects.Text;
  private pitchCountText!: Phaser.GameObjects.Text;
  private resultText!: Phaser.GameObjects.Text;
  private distanceText!: Phaser.GameObjects.Text;
  private timingText!: Phaser.GameObjects.Text; // タイミング結果表示用（テキスト）
  private strikeText!: Phaser.GameObjects.Text; // ストライク表示用テキスト
  private pitchTypeText!: Phaser.GameObjects.Text; // 投球タイプ表示用テキスト
  private timingBarBg!: Phaser.GameObjects.Image; // タイミングバーの背景（画像）
  private timingBarFill!: Phaser.GameObjects.Rectangle | null; // タイミングバーのフィル（削除済み）
  private timingBarSegments: Phaser.GameObjects.Rectangle[] = []; // タイミングバーの各範囲セグメント（画像に含まれているため削除済み）
  private timingBarPointer!: Phaser.GameObjects.Rectangle; // タイミングバーのポインター
  private angleResultText!: Phaser.GameObjects.Text | null; // 角度結果表示用
  private scoreGainText!: Phaser.GameObjects.Text;
  private debugText!: Phaser.GameObjects.Text; // デバッグ用テキスト
  private playBallButton!: Phaser.GameObjects.Image;
  private pauseButton!: Phaser.GameObjects.Image; // 一時停止ボタン（一時停止中はプレイボタンに切り替え）
  private pausedText!: Phaser.GameObjects.Text; // 「一時停止」テキスト（画面中央に表示）
  private pausedOverlay!: Phaser.GameObjects.Graphics; // 一時停止時の暗いオーバーレイ
  private isPaused: boolean = false; // 一時停止状態
  private soundButton!: Phaser.GameObjects.Image; // サウンドON/OFFボタン
  private isSoundOn: boolean = true; // サウンドON/OFF状態
  private timingBarPixelData: ImageData | null = null; // タイミングバー画像のピクセルデータ
  private timingBarPixelWidth: number = 0;
  private timingBarPixelHeight: number = 0;
  private timingBarGreenCenterRatio: number = 0.5; // 緑ゾーン（Best）の中央位置（左右判定の基準）

  constructor() {
    super({ key: 'GameScene' });
  }

  init(): void {
    this.config = { ...defaultGameConfig };
    this.currentPitch = 0;
    this.totalScore = 0;
    this.pitchResults = [];
    this.strikes = 0; // ストライクカウントをリセット
    this.gameState = 'waiting';
  }

  create(): void {
    const { width, height } = this.scale;

    // 背景色
    this.cameras.main.setBackgroundColor('#799EFF');

    // BGMを再生（TitleSceneから継続、または新規再生）
    try {
      const existingBgm = this.sound.get('bgm');
      if (!existingBgm || !existingBgm.isPlaying) {
        const bgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
        bgm.play();
      }
    } catch (e) {
      // エラーが発生した場合は新規に追加
      const bgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
      bgm.play();
    }

    // 地面の高さ
    const groundHeight = height * 0.3;
    const groundY = height - groundHeight; // 地面のY座標（画面座標、下端からの距離）

    // 背景画像をタイルスプライトで表示（地面の上に配置）
    // 横方向のみタイル構造、縦方向は1枚の画像を表示
    const bgOffsetY = 130; // 画面の上端から130ピクセル下に配置
    // 画像の実サイズを取得
    const bgTexture = this.textures.get('bg');
    const bgImageHeight = bgTexture ? bgTexture.source[0].height : (groundY - bgOffsetY);
    this.bg = this.add.tileSprite(
      0, // 画面の左端（カメラ座標）
      bgOffsetY, // 画面の上端より上に配置
      width, // 画面幅（カメラ座標）- 横方向にタイル
      bgImageHeight, // 高さ：画像の実サイズのまま
      'bg'
    );
    this.bg.setOrigin(0, 0); // 左上を原点に
    // 横方向のみタイル、縦方向は実サイズのまま（スケール調整なし）
    this.bg.setTileScale(1, 1);
    // 背景画像のスケールを1.5倍に
    this.bg.setScale(1.5, 1.5);
    this.bg.setDepth(-1); // 地面より後ろに配置
    // 背景を画面に固定（カメラの移動に影響されない）
    this.bg.setScrollFactor(0, 0);

    // 地面をタイルスプライトで表示（画面幅で表示）
    this.groundWidth = width * 10;
    // 地面の位置を画面座標で固定（カメラの移動に影響されないように）
    // 画面の左下に配置（カメラ座標系）
    this.ground = this.add.tileSprite(
      0, // 画面の左端（カメラ座標）
      groundY,
      width, // 画面幅（カメラ座標）
      groundHeight,
      'ground'
    );
    this.ground.setOrigin(0, 0);
    this.ground.setDepth(0);
    // 地面を画面に固定（カメラの移動に影響されない）
    this.ground.setScrollFactor(0, 0);
    // 画像の高さが2倍になったので、setTileScale(1, 1)で1段表示になる
    this.ground.setTileScale(1, 1);

    // ピッチャー位置（左側、地面の上）
    this.ballStartX = width * 0.2;
    this.ballStartY = groundY + 4; // 地面の上に配置（4px下げる）

    // バッター位置（右側、地面の上）
    this.ballTargetX = width * 0.8;
    this.ballTargetY = groundY + 4; // 地面の上に配置（4px下げる）

    // ピッチャー表示（左側）- スプライトシートを使用、地面に設置
    this.pitcher = this.add
      .sprite(this.ballStartX, this.ballStartY, 'pitcher_ss')
      .setDepth(1); // ボールより後ろに表示
    // 元のサイズで表示（177x150ピクセル、リサイズせず使用）
    this.pitcher.setOrigin(0.5, 1); // 下部を基準に配置（地面に設置）
    // ピッチャーを画面に固定（カメラの移動に影響されない）
    this.pitcher.setScrollFactor(0, 0);

    // ピッチャーのアニメーションを作成
    // スプライトシートは885x155ピクセル、5コマ横並び（1コマあたり177x155ピクセル）
    // 5コマすべてを順に再生する投球アニメーション（ループなし、投球ごとに1度再生）
    // 各コマの再生時間を個別に設定
    if (!this.anims.exists('pitcher_throw')) {
      // 各フレームに個別のdurationを設定
      const frameConfigs = [
        { key: 'pitcher_ss', frame: 0, duration: 500 }, // 1コマ目：0.5秒
        { key: 'pitcher_ss', frame: 1, duration: 400 }, // 2コマ目：0.4秒
        { key: 'pitcher_ss', frame: 2, duration: 300 }, // 3コマ目：0.3秒
        { key: 'pitcher_ss', frame: 3, duration: 100 }, // 4コマ目：0.1秒
        { key: 'pitcher_ss', frame: 4, duration: 100 }, // 5コマ目：0.1秒（最後のコマ）
      ];

      console.log('[Pitcher Animation] フレーム設定:', frameConfigs);

      this.anims.create({
        key: 'pitcher_throw',
        frames: frameConfigs,
        repeat: 0, // 1回のみ再生（ループなし）
      });

      console.log('[Pitcher Animation] アニメーション作成完了:', {
        animationKey: 'pitcher_throw',
        frameCount: frameConfigs.length,
        animExists: this.anims.exists('pitcher_throw'),
      });
    } else {
      console.log('[Pitcher Animation] アニメーションは既に存在します');
    }

    // バッター表示（右側）- スプライトシートを使用、地面に設置
    this.batter = this.add
      .sprite(this.ballTargetX, this.ballTargetY, 'batter_ss')
      .setDepth(1); // ボールより後ろに表示
    // 初期フレームを設定（最初のコマを表示）
    this.batter.setFrame(0);
    // 元のサイズで表示（137x162ピクセル、リサイズせず使用）
    this.batter.setOrigin(0.5, 1); // 下部を基準に配置（地面に設置）
    // バッターを画面に固定（カメラの移動に影響されない）
    this.batter.setScrollFactor(0, 0);

    // バッターのスイングアニメーションを作成
    // スプライトシートは1コマあたり137x162ピクセル（リサイズせず使用）
    // 6コマすべてを順に再生するスイングアニメーション（ループなし、スイングごとに1度再生）
    if (!this.anims.exists('batter_swing')) {
      // 6コマすべてを使用（フレーム0-5）
      const frames = this.anims.generateFrameNumbers('batter_ss', { start: 0, end: 5 });
      console.log('[Batter Animation] 生成されたフレーム数:', frames.length);

      this.anims.create({
        key: 'batter_swing',
        frames: frames, // 6コマ（0-5）を順に再生
        frameRate: 15, // アニメーション速度（1秒間に15フレーム）
        repeat: 0, // 1回のみ再生（ループなし）
      });

      console.log('[Batter Animation] アニメーション作成完了:', {
        animationKey: 'batter_swing',
        frameCount: frames.length,
        animExists: this.anims.exists('batter_swing'),
      });
    }

    // キャッチャー表示（バッターの後ろ、さらに右側）- 画像を使用、地面に設置
    const catcherX = width * 0.92; // バッターより右側
    this.catcher = this.add
      .image(catcherX, this.ballTargetY, 'catcher')
      .setDepth(2); // バッターより後ろ（depthを低く）
    // 画像サイズはリサイズせずそのまま使用
    this.catcher.setOrigin(0.5, 1); // 下部を基準に配置（地面に設置）
    // キャッチャーを画面に固定（カメラの移動に影響されない）
    this.catcher.setScrollFactor(0, 0);

    // バット表示（バッターの前に）- 削除
    // this.bat = this.add
    //   .rectangle(this.ballTargetX - 30, this.ballTargetY - 40, 10, 60, 0x654321)
    //   .setDepth(4);
    // // バットを画面に固定（カメラの移動に影響されない）
    // this.bat.setScrollFactor(0, 0);
    // バットは削除（nullのまま）

    // 初期配置の画面座標を保存（打球終了時に戻すため）
    this.pitcherScreen = { x: this.pitcher.x, y: this.pitcher.y };
    this.batterScreen = { x: this.batter.x, y: this.batter.y };
    this.catcherScreen = { x: this.catcher.x, y: this.catcher.y };
    // this.batScreen = { x: this.bat.x, y: this.bat.y };
    this.batScreen = undefined; // バットを削除したのでundefinedに

    // ボール（ピッチャーから投げられる）- 地面から離して配置
    const ballY = groundY - 80; // 地面から80ピクセル上
    this.ball = this.add
      .image(this.ballStartX, ballY, 'ball')
      .setDepth(2);
    // 画像サイズはリサイズせずそのまま使用（30x30ピクセル）
    // ボールを画面に固定（カメラの移動に影響されない）
    this.ball.setScrollFactor(0, 0);
    // 初期状態ではボールを非表示（Play ballボタンをタップするまで表示しない）
    this.ball.setVisible(false);

    // 打球（初期はnull）
    this.hitBall = null;
    this.hitBallBody = null;
    this.groundBody = null;
    this.ballLanded = false;
    this.ballStopped = false;

    // 地面の物理ボディは初期作成しない（flyBall()で再作成する際に正しい位置に配置する）
    // 初期状態では物理ボディを作成しない
    this.groundBody = null;
    // 見た目はTileSpriteを使うので、物理ボディは非表示にする必要はない（画像オブジェクトではない）

    // 角度メーターを作成（右下の角を原点とした1/4円）
    // 右下の角を原点として配置
    this.angleMeterOriginX = width - 20; // 右下の角（右端から20ピクセル内側）
    this.angleMeterOriginY = height - 20; // 右下の角（下端から20ピクセル内側）

    // 角度メーターの背景（画像を使用、200x200ピクセル）
    this.angleBarBg = this.add.image(
      this.angleMeterOriginX,
      this.angleMeterOriginY,
      'angle'
    );

    // 画像サイズを200x200に設定（リサイズせずそのまま使用）
    // 画像のサイズから半径を設定（200x200なので、半径は200）
    this.angleMeterRadius = 200;

    // 画像の原点を右下に設定（角度メーターの原点と一致させる）
    this.angleBarBg.setOrigin(1, 1); // 右下を原点に
    this.angleBarBg.setDepth(10000);
    this.angleBarBg.setVisible(true);
    // 角度メーターを画面に固定（カメラの移動に影響されない）
    this.angleBarBg.setScrollFactor(0, 0);

    // ポインター（針）- 右下の角から伸びる太い線
    this.angleMeterPointer = this.add.line(
      0, 0,
      this.angleMeterOriginX, this.angleMeterOriginY, // 原点
      this.angleMeterOriginX, this.angleMeterOriginY, // 初期位置（原点と同じ）
      0x000000, // 黒色
      1.0
    );
    this.angleMeterPointer.setLineWidth(8); // 太い線
    this.angleMeterPointer.setDepth(10001);
    this.angleMeterPointer.setVisible(true);
    // 角度メーターのポインターを画面に固定（カメラの移動に影響されない）
    this.angleMeterPointer.setScrollFactor(0, 0);

    // カーソル（削除 - ポインターで代用）
    this.angleBarCursor = null;

    // Best帯は画像に含まれているため、Graphicsでの描画は不要
    // 画像に25度〜35度が緑で表示されている
    this.bestBandArc = null; // 画像に含まれているため不要

    // 初期化：時間を0に設定して動きを開始
    this.angleBarTime = 0;
    this.angleBarSpeed = 0.1;
    this.updateCallCount = 0;

    // UI要素
    const safeArea = 20; // 左上のマージン
    this.scoreText = this.add
      .text(safeArea, safeArea, `スコア: ${this.totalScore}`, {
        fontSize: '40px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
      })
      .setDepth(10)
      .setScrollFactor(0, 0); // 画面に固定

    // BallsをScoreの下に配置（フォントサイズに合わせて間隔を調整）
    // 10球制限を撤廃したため、Balls表示は削除
    // this.pitchCountText = this.add
    //   .text(safeArea, safeArea + 45, `Balls: ${this.config.totalPitches}`, {
    //     fontSize: '50px',
    //     fontFamily: 'Micro5',
    //     color: '#ffff00', // ファミコン風の黄色
    //   })
    //   .setDepth(10)
    //   .setScrollFactor(0, 0); // 画面に固定

    // ストライク表示を追加（Scoreの下に配置、行間50pxで統一）
    this.strikeText = this.add
      .text(safeArea, safeArea + 50, 'ストライク: 0', {
        fontSize: '40px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
      })
      .setDepth(10)
      .setScrollFactor(0, 0); // 画面に固定

    // PA表示を追加（Strikesの下に配置、行間50pxで統一）
    this.pitchCountText = this.add
      .text(safeArea, safeArea + 100, '打席: 0', {
        fontSize: '40px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
      })
      .setDepth(10)
      .setScrollFactor(0, 0); // 画面に固定

    // 投球タイプ表示を追加（画面中央に大きく表示）
    this.pitchTypeText = this.add
      .text(width / 2, height / 2, '', {
        fontSize: '80px',
        fontFamily: 'YuseiMagic',
        color: '#ffffff', // ファミコン風の白色
      })
      .setDepth(10)
      .setOrigin(0.5, 0.5) // 中央揃え
      .setScrollFactor(0, 0) // 画面に固定
      .setVisible(false); // 初期は非表示（投球開始時に表示）

    // 結果表示用テキスト（初期は非表示）- ホームラン用（100px）
    this.resultText = this.add
      .text(width / 2, height * 0.3, '', {
        fontSize: '100px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(10)
      .setVisible(false)
      .setScrollFactor(0, 0); // 画面に固定

    this.distanceText = this.add
      .text(width / 2, height * 0.4, '', {
        fontSize: '40px',
        fontFamily: 'YuseiMagic',
        color: '#ffffff', // ファミコン風の白
      })
      .setOrigin(0.5)
      .setDepth(10)
      .setVisible(false)
      .setScrollFactor(0, 0); // 画面に固定

    // タイミングバー（横長）を作成
    // 左端をボール表示位置（ballStartX）に、右端をキャッチャー位置に合わせる
    this.timingBarStartX = this.ballStartX; // ボールの開始位置（左端）
    this.timingBarEndX = this.catcherScreen ? this.catcherScreen.x : width * 0.92; // キャッチャーの位置（右端）
    this.timingBarWidth = this.timingBarEndX - this.timingBarStartX; // バーの幅（キャッチャー位置 - ボール位置）
    const timingBarHeight = 20; // バーの高さ
    const timingBarX = this.timingBarStartX + this.timingBarWidth / 2; // バーのX座標（中央）
    const timingBarY = groundY + 60; // バーのY座標（ピッチャーとバッターの少し下）

    // タイミング結果表示用テキスト（タイミングバーの下に表示、ファミコン風）
    this.timingText = this.add
      .text(width / 2, timingBarY + 48, '', {
        fontSize: '30px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(10)
      .setVisible(false)
      .setScrollFactor(0, 0); // 画面に固定

    // タイミングバーの背景（画像を使用、524x36ピクセル、リサイズせず使用）
    this.timingBarBg = this.add
      .image(timingBarX, timingBarY, 'timing_bar')
      .setOrigin(0.5, 0.5)
      .setDepth(10)
      .setVisible(true)
      .setScrollFactor(0, 0); // 画面に固定
    // 画像サイズはリサイズせずそのまま使用（524x36ピクセル）
    this.cacheTimingBarPixels();

    // セグメントは画像に含まれているため不要
    this.timingBarSegments = [];

    // 旧ベスト帯は画像に含まれているため不要
    this.timingBarFill = null;

    // タイミングポインター（現在のタイミング位置を示す、ファミコン風の白）
    this.timingBarPointer = this.add
      .rectangle(timingBarX, timingBarY, 6, timingBarHeight + 12, 0xffffff)
      .setOrigin(0.5, 0.5)
      .setDepth(12)
      .setVisible(false)
      .setScrollFactor(0, 0); // 画面に固定
    // ボーダーを追加（ファミコン風）
    this.timingBarPointer.setStrokeStyle(2, 0x000000);

    // タイミングバーを表示（ゲーム画面に遷移した時点で表示）
    this.timingBarBg.setVisible(true);
    // セグメントは画像に含まれているため不要
    // ポインターは投球開始時に表示される

    // Score表示用テキスト（+ n用、100px）
    this.scoreGainText = this.add
      .text(width / 2, height * 0.3 + 140, '', {
        fontSize: '100px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
      })
      .setOrigin(0.5)
      .setDepth(10)
      .setVisible(false)
      .setScrollFactor(0, 0); // 画面に固定

    // デバッグ用テキスト（左下に表示、打球速度、飛距離、角度）
    const debugTextMargin = 20;
    const debugTextBottomMargin = 50; // 下からのマージン（少し下に表示）
    const debugTextY = height - debugTextBottomMargin;
    this.debugText = this.add
      .text(debugTextMargin, debugTextY, '打球速度: 0 km/h\n飛距離: 0 m\n角度: 0°', {
        fontSize: '50px',
        fontFamily: 'YuseiMagic',
        color: '#ffff00', // ファミコン風の黄色
      })
      .setOrigin(0, 1) // 左下基準
      .setDepth(10)
      .setVisible(true)
      .setScrollFactor(0, 0); // 画面に固定

    // Play Ballボタンを作成（画像ボタン）
    this.playBallButton = this.add
      .image(width / 2, height / 2, 'playball')
      .setDepth(10003)
      .setScrollFactor(0, 0) // 画面に固定
      .setInteractive({ useHandCursor: true });

    // ボタンのホバー効果（画像を切り替え）
    this.playBallButton.on('pointerover', () => {
      this.playBallButton.setTexture('playball_hover');
    });
    this.playBallButton.on('pointerout', () => {
      this.playBallButton.setTexture('playball');
    });

    // ボタンクリックで投球開始
    const handlePlayBallClick = (pointer: Phaser.Input.Pointer, localX: number, localY: number, event: Phaser.Types.Input.EventData) => {
      // イベントの伝播を停止（onPointerDownが呼ばれないように）
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }

      console.log('Play Ballボタンがクリックされました - ボタンを非表示にします');

      // ボタンタップ音を再生
      this.sound.play('button', { volume: 0.5 });

      // Play Ballボタンを非表示に
      if (this.playBallButton) {
        this.playBallButton.setVisible(false);
        this.playBallButton.setActive(false);
      }

      // 一時停止ボタンを表示（ゲーム開始後は有効化）
      if (this.pauseButton) {
        this.pauseButton.setVisible(true);
      }
      // サウンドボタンを表示（ゲーム開始後は有効化）
      if (this.soundButton) {
        this.soundButton.setVisible(true);
      }

      // 投球開始（アニメーション再生はstartNextPitch()内で行う）
      this.startNextPitch();
    };

    this.playBallButton.on('pointerdown', handlePlayBallClick);

    // 一時停止ボタンを作成（初期は非表示、Play ballボタンをタップするまで無効）
    const pauseButtonSize = 50;
    const pauseButtonX = width - pauseButtonSize - 20;
    const pauseButtonY = pauseButtonSize + 20;
    
    // サウンドON/OFFボタンを作成（一時停止ボタンの左横に配置）
    const buttonSize = 50;
    const buttonSpacing = 10; // ボタン間の間隔
    const soundButtonX = pauseButtonX - buttonSize - buttonSpacing; // 一時停止ボタンの左横
    const soundButtonY = pauseButtonY; // 一時停止ボタンと同じY座標
    
    this.soundButton = this.add
      .image(soundButtonX, soundButtonY, 'sounds_on')
      .setDisplaySize(buttonSize, buttonSize)
      .setDepth(10005)
      .setScrollFactor(0, 0) // 画面に固定
      .setInteractive({ useHandCursor: true })
      .setVisible(false); // 初期は非表示
    // 現在のサウンド状態に合わせて初期表示を同期
    const isMuted = this.sound.mute;
    this.isSoundOn = !isMuted && this.sound.volume > 0;
    this.soundButton.setTexture(this.isSoundOn ? 'sounds_on' : 'sounds_off');

    // サウンドボタンのホバー効果
    this.soundButton.on('pointerover', () => {
      if (this.gameState !== 'waiting') {
        this.soundButton.setAlpha(0.7);
      }
    });
    this.soundButton.on('pointerout', () => {
      this.soundButton.setAlpha(1.0);
    });

    // サウンドボタンのクリック処理
    this.soundButton.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // イベントの伝播を停止してスイング判定を防ぐ
      pointer.event.stopPropagation();
      // サウンドON/OFFを切り替え（切り替え前に現在の状態を確認してボタン音を再生）
      const wasSoundOn = this.isSoundOn;
      this.toggleSound();
      // 切り替え後、サウンドがONになった場合はボタン音を再生
      if (this.isSoundOn && !wasSoundOn) {
        this.sound.play('button', { volume: 0.5 });
      }
    });

    // 一時停止ボタンを作成（初期は非表示、Play ballボタンをタップするまで無効）
    this.pauseButton = this.add
      .image(pauseButtonX, pauseButtonY, 'stop')
      .setDisplaySize(pauseButtonSize, pauseButtonSize)
      .setDepth(10005)
      .setScrollFactor(0, 0) // 画面に固定
      .setInteractive({ useHandCursor: true })
      .setVisible(false); // 初期は非表示

    // 一時停止ボタンのホバー効果
    this.pauseButton.on('pointerover', () => {
      // ゲーム開始前はホバー効果を無効化
      if (this.gameState !== 'waiting') {
        this.pauseButton.setAlpha(0.7);
      }
    });
    this.pauseButton.on('pointerout', () => {
      this.pauseButton.setAlpha(1.0);
    });

    // 一時停止ボタンのクリック処理
    this.pauseButton.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Play ballボタンをタップするまでは無効
      if (this.gameState === 'waiting') {
        return;
      }
      // イベントの伝播を停止してスイング判定を防ぐ
      pointer.event.stopPropagation();
      // ボタンタップ音を再生（サウンドONの場合のみ）
      if (this.isSoundOn) {
        this.sound.play('button', { volume: 0.5 });
      }
      if (this.isPaused) {
        // 一時停止中は再開
        this.resumeGame();
      } else {
        // 通常時は一時停止
        this.pauseGame();
      }
    });

    // 一時停止時の暗いオーバーレイを作成（初期は非表示）
    this.pausedOverlay = this.add.graphics();
    this.pausedOverlay.fillStyle(0x000000, 0.5); // 半透明の黒
    this.pausedOverlay.fillRect(0, 0, width, height);
    this.pausedOverlay.setDepth(10005); // 一時停止テキストより下
    this.pausedOverlay.setVisible(false);
    this.pausedOverlay.setScrollFactor(0, 0); // 画面に固定

    // 「一時停止」テキストを作成（初期は非表示、球種表示と被らないように上に配置）
    this.pausedText = this.add.text(
      width / 2,
      height * 0.25,
      '一時停止',
      {
        fontSize: '80px',
        fontFamily: 'YuseiMagic',
        color: '#ffffff',
        fontStyle: 'bold',
      }
    );
    this.pausedText.setOrigin(0.5);
    this.pausedText.setDepth(10006);
    this.pausedText.setVisible(false);
    this.pausedText.setScrollFactor(0, 0); // 画面に固定

    // 入力処理（長押し方式）
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointerup', this.onPointerUp, this);

    // ゲーム開始時のカメラの縦位置を保存
    this.gameStartCamY = this.cameras.main.scrollY;
  }

  private startNextPitch(): void {
    console.log('[startNextPitch] 開始:', {
      currentPitch: this.currentPitch,
      totalPitches: this.config.totalPitches,
    });

    // 投球速度を打席数に応じて選択
    const rand = Math.random();
    let pitchType: 'ultraFast' | 'fast' | 'slow' | 'knuckleball1' | 'knuckleball2' | 'knuckleball3' | 'knuckleball4' | 'knuckleball5' | 'knuckleball6';
    
    // 1-3打席目：ファストボールのみ
    if (this.currentPitch < 3) {
      pitchType = 'fast';
    }
    // 4-10打席目：スローボール、ファストボール、パワーファストボールをランダム
    else if (this.currentPitch < 10) {
      if (rand < 0.33) {
        pitchType = 'slow';
      } else if (rand < 0.67) {
        pitchType = 'fast';
      } else {
        pitchType = 'ultraFast';
      }
    }
    // 11打席目以降：すべての球種をランダム
    else {
      const allPitchTypes: typeof pitchType[] = [
        'slow',
        'fast',
        'ultraFast',
        'knuckleball1',
        'knuckleball2',
        'knuckleball3',
        'knuckleball4',
        'knuckleball5',
        'knuckleball6',
      ];
      const randomIndex = Math.floor(rand * allPitchTypes.length);
      pitchType = allPitchTypes[randomIndex];
    }
    
    this.currentPitchType = pitchType;
    
    // 投球速度を設定
    if (pitchType === 'ultraFast') {
      this.currentPitchSpeed = 1000;
    } else if (pitchType === 'fast') {
      this.currentPitchSpeed = 600;
    } else if (pitchType === 'knuckleball1' || pitchType === 'knuckleball2') {
      this.currentPitchSpeed = 400; // 魔球1、2の基本速度
    } else if (pitchType === 'knuckleball3') {
      this.currentPitchSpeed = 350; // 魔球3の基本速度（途中から加速）
    } else if (pitchType === 'knuckleball4') {
      this.currentPitchSpeed = 500; // 魔球4の基本速度（分身）
    } else if (pitchType === 'knuckleball5') {
      this.currentPitchSpeed = 1800; // 魔球5の基本速度（超速球）
    } else if (pitchType === 'knuckleball6') {
      this.currentPitchSpeed = 400; // 魔球6の基本速度（カクカク進む）
    } else {
      this.currentPitchSpeed = 350;
    }
    
    // 投球タイプを表示
    if (this.pitchTypeText) {
      let typeLabel: string;
      let color: string;
      if (pitchType === 'ultraFast') {
        typeLabel = '豪速球';
        color = '#ff00ff'; // マゼンタ（豪速球）
      } else if (pitchType === 'fast') {
        typeLabel = 'ファストボール';
        color = '#ff0000'; // 赤色（速球）
      } else if (pitchType === 'knuckleball1') {
        typeLabel = '魔球モドリン';
        color = '#00ff00'; // 緑色（魔球1）
      } else if (pitchType === 'knuckleball2') {
        typeLabel = '魔球キエルン';
        color = '#ffff00'; // 黄色（魔球2）
      } else if (pitchType === 'knuckleball3') {
        typeLabel = '魔球カッソゥ';
        color = '#ff8800'; // オレンジ（魔球3）
      } else if (pitchType === 'knuckleball4') {
        typeLabel = '魔球ブンシーン';
        color = '#ff00ff'; // マゼンタ（魔球4：分身）
      } else if (pitchType === 'knuckleball5') {
        typeLabel = '魔球のぞみ';
        color = '#ff0000'; // 赤色（魔球5：超速球）
      } else if (pitchType === 'knuckleball6') {
        typeLabel = '魔球一時停止';
        color = '#00ffff'; // シアン（魔球6：カクカク進む）
      } else {
        typeLabel = 'スローボール';
        color = '#0088ff'; // 青色（スローボール）
      }
      this.pitchTypeText.setText(typeLabel);
      this.pitchTypeText.setVisible(true);
      this.pitchTypeText.setColor(color);
      // 5pxの白縁を追加
      this.pitchTypeText.setStroke('#ffffff', 5);
    }
    
    console.log('[startNextPitch] 投球速度設定:', {
      pitchType,
      pitchSpeed: this.currentPitchSpeed,
    });

    // 前回のスイング開始位置をリセット
    this.hasSwingBallPos = false;
    // タイミングバーのポインターの最終位置をリセット
    this.finalTimingPointerX = null;
    // 空振りフラグをリセット
    this.isSwingMissed = false;
    // 保存した結果をクリア
    (this as any).pendingMissResult = null;

    // 打球速度、飛距離、角度を0にリセット
    (this as any).displaySpeedKmh = null;
    (this as any).displayDistanceMx = null;
    (this as any).finalDisplayDistanceMx = null;
    (this as any).flyBallAngle = null;
    (this as any).flyBallStartX = null;
    (this as any).flyBallStartY = null;

    // タイミングバーのポインター位置をリセット（左端に戻す）
    if (this.timingBarPointer) {
      this.timingBarPointer.setX(this.timingBarStartX); // 左端（0%の位置）にリセット
    }
    // タイミング: n表記を非表示
    if (this.timingText) {
      this.timingText.setVisible(false);
    }

    console.log('[startNextPitch] フラグをリセット:', {
      hasSwingBallPos: this.hasSwingBallPos,
      isSwingMissed: this.isSwingMissed,
      pendingMissResult: (this as any).pendingMissResult,
    });

    // 念のため、ピッチャー/バッター/キャッチャー/バットを「画面固定」に戻す（状態が壊れても次打席で復旧）
    if (this.pitcher && this.pitcherScreen) {
      this.pitcher.setScrollFactor(0, 0);
      this.pitcher.setPosition(this.pitcherScreen.x, this.pitcherScreen.y);
      this.pitcher.setVisible(true);
    }
    if (this.batter && this.batterScreen) {
      this.batter.setScrollFactor(0, 0);
      this.batter.setPosition(this.batterScreen.x, this.batterScreen.y);
      this.batter.setVisible(true);
      // バッターのアニメーションをフレーム0にリセット
      if (this.batter.anims) {
        this.batter.anims.stop();
        this.batter.setFrame(0);
      }
    }

    // ピッチャーの投球アニメーションを開始（次の打席でも再生）
    console.log('[startNextPitch] ピッチャーアニメーション確認:', {
      pitcherExists: !!this.pitcher,
      animExists: this.anims.exists('pitcher_throw'),
      pitcherTexture: this.pitcher?.texture?.key,
    });

    if (this.pitcher && this.anims.exists('pitcher_throw')) {
      console.log('[startNextPitch] 投球アニメーション開始');

      // 既存のイベントリスナーをクリア（重複防止）
      this.pitcher.off('animationupdate');
      this.pitcher.off('animationcomplete');
      this.pitcher.off('animationstart');

      // アニメーションイベントを監視（再生前に設定）
      this.pitcher.on('animationupdate', (animation: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        console.log('[Pitcher Animation] フレーム更新:', {
          frameIndex: frame.index,
          frameKey: frame.frame.name,
          currentFrame: this.pitcher.frame.name,
        });

        // フレーム4（5コマ目）が表示されたら、その再生時間後に投球を開始
        if (frame.index === 4) {
          console.log('[Pitcher Animation] フレーム4（5コマ目）表示、0.1秒後に投球開始');
          // フレーム4の再生時間（0.1秒）後に投球を開始
          this.time.delayedCall(100, () => {
            console.log('[Pitcher Animation] フレーム4再生完了、ボールを投げます');
            this.startPitchAfterAnimation();
          });
        }
      });

      // アニメーション開始イベント
      this.pitcher.once('animationstart', () => {
        console.log('[Pitcher Animation] アニメーション開始');
      });

      // アニメーション完了イベント（念のため、投球は既に開始されているはず）
      this.pitcher.once('animationcomplete', () => {
        console.log('[Pitcher Animation] アニメーション完了');
      });

      // アニメーションを停止してから再生（最初から再生）
      if (this.pitcher.anims.isPlaying) {
        console.log('[startNextPitch] 既存のアニメーションを停止');
        this.pitcher.anims.stop();
      }

      // アニメーションを再生（最初から再生）
      console.log('[startNextPitch] アニメーション再生開始');
      const playResult = this.pitcher.play('pitcher_throw');
      console.log('[startNextPitch] アニメーション再生結果:', {
        playResult,
        isPlaying: this.pitcher.anims.isPlaying,
        currentAnim: this.pitcher.anims.currentAnim?.key,
      });
    } else {
      // アニメーションが存在しない場合は即座に投球開始
      console.warn('[startNextPitch] アニメーションが存在しないため、即座に投球開始', {
        pitcherExists: !!this.pitcher,
        animExists: this.anims.exists('pitcher_throw'),
      });
      this.startPitchAfterAnimation();
    }
  }

  // アニメーション完了後の実際の投球処理
  private startPitchAfterAnimation(): void {
    if (this.batter && this.batterScreen) {
      this.batter.setScrollFactor(0, 0);
      this.batter.setPosition(this.batterScreen.x, this.batterScreen.y);
      this.batter.setVisible(true);
      // バッターのアニメーションをフレーム0にリセット
      if (this.batter.anims) {
        this.batter.anims.stop();
        this.batter.setFrame(0);
      }
    }
    if (this.catcher && this.catcherScreen) {
      this.catcher.setScrollFactor(0, 0);
      this.catcher.setPosition(this.catcherScreen.x, this.catcherScreen.y);
      this.catcher.setRotation(0); // 回転をリセット
      this.catcher.setVisible(true);
    }
    // バットは削除済み
    // if (this.bat && this.batScreen) {
    //   this.bat.setScrollFactor(0, 0);
    //   this.bat.setPosition(this.batScreen.x, this.batScreen.y);
    //   this.bat.setVisible(true);
    // }

    this.currentPitch++;
    // 打席数を更新
    if (this.pitchCountText) {
      this.pitchCountText.setText(`打席: ${this.currentPitch}`);
    }
    this.gameState = 'pitching';
    this.tapCount = 0;
    this.isCursorMoving = false; // カーソルを停止

    // 分身ボールをクリーンアップ（前の投球の残りを削除）
    this.cleanupBallClones();

    // ボールを開始位置に配置（地面から離して）
    const { height } = this.scale;
    const groundHeight = height * 0.3;
    const groundY = height - groundHeight;
    const ballY = groundY - 80; // 地面から80ピクセル上

    // ボールが破棄されている場合は再作成
    if (!this.ball) {
      this.ball = this.add
        .image(this.ballStartX, ballY, 'ball')
        .setDepth(2);
      // 画像サイズはリサイズせずそのまま使用（30x30ピクセル）
      this.ball.setScrollFactor(0, 0); // 画面に固定
    } else {
      this.ball.setPosition(this.ballStartX, ballY);
      this.ball.setVisible(true);
    }

    // 角度バーは常に表示（非表示にしない）
    // Play Ballボタンは非表示のまま（次の投球まで表示しない）
    // ボタンは手動で表示する必要がある場合のみ再表示

    // タップカウントをリセット
    this.tapCount = 0;

    // 結果テキストを非表示
    this.resultText.setVisible(false);
    this.distanceText.setVisible(false);
    this.scoreGainText.setVisible(false);
    this.timingText.setVisible(false);
    // タイミングバーを表示（ゲーム画面に遷移した時点で表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    // セグメントとベスト帯は画像に含まれているため不要
    // タイミングバーのポインターをリセット（位置を左端に戻し、非表示）
    if (this.timingBarPointer) {
      this.timingBarPointer.setX(this.timingBarStartX); // 左端（0%の位置）にリセット
      this.timingBarPointer.setVisible(false);
    }
    if (this.angleResultText) {
      this.angleResultText.setVisible(false);
    }
    // 打席数を表示
    if (this.pitchCountText) {
      this.pitchCountText.setText(`打席: ${this.currentPitch}`);
      this.pitchCountText.setVisible(true);
    }
    // デバッグテキストは常に表示（初期値：0）
    this.debugText.setText(
      `打球速度: 0 km/h\n飛距離: 0 m\n角度: 0°`
    );
    this.debugText.setVisible(true);

    // 投球時間を計算（距離 / 速度）
    // キャッチャーの縦中央に向かうように設定
    const catcherCenterX = this.catcherScreen ? this.catcherScreen.x : this.ballTargetX;
    const catcherCenterY = this.catcherScreen ? this.catcherScreen.y - 45 : this.ballTargetY; // キャッチャーの縦中央（高さ90pxの半分）
    const distance = Phaser.Math.Distance.Between(
      this.ballStartX,
      this.ballStartY,
      catcherCenterX,
      catcherCenterY
    );
    const pitchDuration = (distance / this.currentPitchSpeed) * 1000; // ミリ秒（現在の投球速度を使用）
    this.pitchDuration = pitchDuration; // 投球時間を保存（タイミング判定で使用）

    // パーフェクトタイミングは投球開始からキャッチャー到達までの80%の位置
    // 0 = 投球開始、100 = キャッチャー到達（キャッチャーミット）、80 = ベストタイミング
    this.perfectTimingTime = pitchDuration * 0.8;
    this.timingStartTime = this.time.now;

    // ピッチャーがボールを投げた音を再生
    this.sound.play('pitch', { volume: 0.5 });

    // 魔球の場合は特殊な動作を実装
    if (this.currentPitchType === 'knuckleball1') {
      this.startKnuckleball1Pitch(catcherCenterX, catcherCenterY, distance);
    } else if (this.currentPitchType === 'knuckleball2') {
      this.startKnuckleball2Pitch(catcherCenterX, catcherCenterY, distance);
    } else if (this.currentPitchType === 'knuckleball3') {
      this.startKnuckleball3Pitch(catcherCenterX, catcherCenterY, distance);
    } else if (this.currentPitchType === 'knuckleball4') {
      this.startKnuckleball4Pitch(catcherCenterX, catcherCenterY, distance);
    } else if (this.currentPitchType === 'knuckleball5') {
      this.startKnuckleball5Pitch(catcherCenterX, catcherCenterY, distance);
    } else if (this.currentPitchType === 'knuckleball6') {
      this.startKnuckleball6Pitch(catcherCenterX, catcherCenterY, distance);
    } else {
      // 通常の投球
      this.startNormalPitch(catcherCenterX, catcherCenterY, pitchDuration);
    }
  }

  // 通常の投球を開始
  private startNormalPitch(catcherCenterX: number, catcherCenterY: number, pitchDuration: number): void {
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      // 投球タイプを少し表示してから非表示にする
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }

    // ボールをキャッチャーの縦中央に向かって移動
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: catcherCenterX,
      y: catcherCenterY,
      duration: pitchDuration,
      onComplete: () => {
        this.handleBallReachedCatcher();
      },
    });

    // タイミング判定可能状態に移行（ボールが一定距離まで来たら）
    this.time.delayedCall(pitchDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球1：球が途中で一度ピッチャー側に戻る
  private startKnuckleball1Pitch(catcherCenterX: number, catcherCenterY: number, totalDistance: number): void {
    const startX = this.ballStartX;
    // 通常の投球と同じY座標を使用（地面から80ピクセル上）
    const { height } = this.scale;
    const groundHeight = height * 0.3;
    const groundY = height - groundHeight;
    const startY = groundY - 80; // 通常の投球と同じ位置
    
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }
    
    // 魔球1の動作：カーソルが「とても早い」範囲（65-72%）より少し前で少し戻る
    // Y座標はcatcherCenterYで固定（通常の投球と同じY座標を維持）
    const forwardPointX = startX + (catcherCenterX - startX) * 0.6; // 60%地点まで進む（とても早い65%より少し前）
    const forwardPointY = catcherCenterY; // Y座標はキャッチャー位置で固定
    const backPointX = startX + (catcherCenterX - startX) * 0.5; // 50%地点まで戻る（戻る距離を伸ばす）
    const backPointY = catcherCenterY; // Y座標はキャッチャー位置で固定
    
    const baseSpeed = this.currentPitchSpeed;
    // Y座標は固定なので、X座標のみで距離を計算
    const distance1 = Math.abs(forwardPointX - startX); // 開始位置から60%地点まで
    const distance2 = Math.abs(backPointX - forwardPointX); // 60%地点から50%地点まで（戻る距離を伸ばす）
    const distance3 = Math.abs(catcherCenterX - backPointX); // 50%地点からキャッチャーへ
    
    const duration1 = (distance1 / baseSpeed) * 1000; // 60%地点まで
    const duration2 = (distance2 / (baseSpeed * 0.9)) * 1000; // 50%地点まで戻る
    const duration3 = (distance3 / (baseSpeed * 1.1)) * 1000; // 再びキャッチャーへ
    
    const totalDuration = duration1 + duration2 + duration3;
    this.pitchDuration = totalDuration;
    this.perfectTimingTime = totalDuration * 0.8;
    
    // 1. ボールが60%地点まで進む（タイミングカーソルはupdate()でボールの位置に連動）
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: forwardPointX,
      y: forwardPointY,
      duration: duration1,
      onComplete: () => {
        // 2. ボールが50%地点まで戻る（タイミングカーソルはupdate()で連動）
        this.ballTween = this.tweens.add({
          targets: this.ball,
          x: backPointX,
          y: backPointY,
          duration: duration2,
          onComplete: () => {
            // 3. ボールが再びキャッチャーへ（タイミングカーソルはupdate()で連動）
            this.ballTween = this.tweens.add({
              targets: this.ball,
              x: catcherCenterX,
              y: catcherCenterY, // Y座標はキャッチャー位置
              duration: duration3,
              onComplete: () => {
                this.handleBallReachedCatcher();
              },
            });
          },
        });
      },
    });

    this.time.delayedCall(totalDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球2：球とタイミングカーソルが消える（実際にはあるのでタイミングよく振れば当たる）
  private startKnuckleball2Pitch(catcherCenterX: number, catcherCenterY: number, totalDistance: number): void {
    const startX = this.ballStartX;
    // ボールの実際のY座標を使用（地面から80ピクセル上）
    const startY = this.ball ? this.ball.y : this.ballStartY;
    
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }
    
    // 魔球2の動作：球とタイミングカーソルが消える（実際にはあるのでタイミングよく振れば当たる）
    // Y座標は通常の投球と同じ（開始: groundY - 80、終了: catcherCenterY）
    const hidePointX = startX + (catcherCenterX - startX) * 0.3; // 30%地点で消える
    const hidePointY = startY + (catcherCenterY - startY) * 0.3; // 開始から終了に向かって30%の位置
    const revealPointX = startX + (catcherCenterX - startX) * 0.7; // 70%地点で再表示
    const revealPointY = startY + (catcherCenterY - startY) * 0.7; // 開始から終了に向かって70%の位置
    
    const baseSpeed = this.currentPitchSpeed;
    const distance1 = Phaser.Math.Distance.Between(startX, startY, hidePointX, hidePointY);
    const distance2 = Phaser.Math.Distance.Between(hidePointX, hidePointY, revealPointX, revealPointY);
    const distance3 = Phaser.Math.Distance.Between(revealPointX, revealPointY, catcherCenterX, catcherCenterY); // 通常の投球と同じ終了位置
    
    const duration1 = (distance1 / baseSpeed) * 1000; // 30%地点まで
    const duration2 = (distance2 / baseSpeed) * 1000; // 非表示のまま進む
    const duration3 = (distance3 / baseSpeed) * 1000; // 再表示してからキャッチャーへ
    
    const totalDuration = duration1 + duration2 + duration3;
    this.pitchDuration = totalDuration;
    this.perfectTimingTime = totalDuration * 0.8;
    
    // 1. ボールが30%地点まで進む
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: hidePointX,
      y: hidePointY,
      duration: duration1,
      onComplete: () => {
        // 2. ボールとタイミングカーソルを非表示にする
        if (this.ball) {
          this.ball.setVisible(false);
        }
        if (this.timingBarPointer) {
          this.timingBarPointer.setVisible(false);
        }
        
        // 3. 非表示のまま進む
        this.ballTween = this.tweens.add({
          targets: this.ball,
          x: revealPointX,
          y: revealPointY,
          duration: duration2,
          onComplete: () => {
            // 4. ボールとタイミングカーソルを再表示する
            if (this.ball) {
              this.ball.setVisible(true);
            }
            if (this.timingBarPointer) {
              this.timingBarPointer.setVisible(true);
            }
            
            // 5. キャッチャーへ（通常の投球と同じ終了位置）
            this.ballTween = this.tweens.add({
              targets: this.ball,
              x: catcherCenterX,
              y: catcherCenterY, // 通常の投球と同じ終了位置
              duration: duration3,
              onComplete: () => {
                this.handleBallReachedCatcher();
              },
            });
          },
        });
      },
    });

    this.time.delayedCall(totalDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球3：途中から加速する
  private startKnuckleball3Pitch(catcherCenterX: number, catcherCenterY: number, totalDistance: number): void {
    const startX = this.ballStartX;
    // ボールの実際のY座標を使用（地面から80ピクセル上）
    const startY = this.ball ? this.ball.y : this.ballStartY;
    
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }
    
    // 魔球3の動作：途中から加速する
    const acceleratePointX = startX + (catcherCenterX - startX) * 0.5; // 50%地点から加速
    const acceleratePointY = startY + (catcherCenterY - startY) * 0.5;
    
    const baseSpeed = this.currentPitchSpeed;
    const distance1 = Phaser.Math.Distance.Between(startX, startY, acceleratePointX, acceleratePointY);
    const distance2 = Phaser.Math.Distance.Between(acceleratePointX, acceleratePointY, catcherCenterX, catcherCenterY);
    
    const duration1 = (distance1 / baseSpeed) * 1000; // 50%地点まで（通常速度）
    const duration2 = (distance2 / (baseSpeed * 2.5)) * 1000; // 加速してキャッチャーへ（2.5倍速）
    
    const totalDuration = duration1 + duration2;
    this.pitchDuration = totalDuration;
    this.perfectTimingTime = totalDuration * 0.8;
    
    // 1. ボールが50%地点まで通常速度で進む
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: acceleratePointX,
      y: acceleratePointY,
      duration: duration1,
      onComplete: () => {
        // 2. 加速してキャッチャーへ
        this.ballTween = this.tweens.add({
          targets: this.ball,
          x: catcherCenterX,
          y: catcherCenterY,
          duration: duration2,
          onComplete: () => {
            this.handleBallReachedCatcher();
          },
        });
      },
    });

    this.time.delayedCall(totalDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球4：分身球（ボールが複数見える、どれが本物か分からない）
  private startKnuckleball4Pitch(catcherCenterX: number, catcherCenterY: number, totalDistance: number): void {
    const startX = this.ballStartX;
    // ボールの実際のY座標を使用（地面から80ピクセル上）
    const startY = this.ball ? this.ball.y : this.ballStartY;
    
    // groundYを取得（startY = groundY - 80）
    const { height } = this.scale;
    const groundHeight = height * 0.3;
    const groundY = height - groundHeight;
    
    // 既存の分身ボールをクリーンアップ
    this.cleanupBallClones();
    
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }
    
    // 分身ボールを2つ作成（異なる軌道で移動）
    const clone2 = this.add.image(startX, startY, 'ball');
    clone2.setDepth(1);
    clone2.setAlpha(0.5);
    clone2.setScrollFactor(0, 0);
    this.ballClones.push(clone2);
    
    const clone3 = this.add.image(startX, startY, 'ball');
    clone3.setDepth(1);
    clone3.setAlpha(0.5);
    clone3.setScrollFactor(0, 0);
    this.ballClones.push(clone3);
    
    // 各ボールの軌道を設定
    const baseSpeed = this.currentPitchSpeed;
    const viaX = startX + (catcherCenterX - startX) * 0.4; // 40%地点のX座標
    
    // 球1（本物）: startY → catcherCenterY（直線）球速+10%
    const speed1 = baseSpeed * 1.1; // 球速+10%
    const distance1 = Phaser.Math.Distance.Between(startX, startY, catcherCenterX, catcherCenterY);
    const pitchDuration = (distance1 / speed1) * 1000;
    this.pitchDuration = pitchDuration;
    this.perfectTimingTime = pitchDuration * 0.8;
    
    // 球2（分身1）: startY → 40%地点で(groundY - 104.4) → groundY - 61 球速+15%
    const viaY2 = groundY - 104.4; // -104.4を経由
    const endY2 = groundY - 61; // 終了位置
    const speed2 = baseSpeed * 1.15; // 球速+15%
    const distance2a = Phaser.Math.Distance.Between(startX, startY, viaX, viaY2);
    const distance2b = Phaser.Math.Distance.Between(viaX, viaY2, catcherCenterX, endY2);
    const duration2a = (distance2a / speed2) * 1000;
    const duration2b = (distance2b / speed2) * 1000;
    
    // 球3（分身2）: startY → 40%地点で(groundY - 24.4) → groundY - 21 球速+15%（球2の反転）
    const viaY3 = groundY - 24.4; // -24.4を経由
    const endY3 = groundY - 21; // 終了位置
    const speed3 = baseSpeed * 1.15; // 球速+15%
    const distance3a = Phaser.Math.Distance.Between(startX, startY, viaX, viaY3);
    const distance3b = Phaser.Math.Distance.Between(viaX, viaY3, catcherCenterX, endY3);
    const duration3a = (distance3a / speed3) * 1000;
    const duration3b = (distance3b / speed3) * 1000;
    
    // 本物のボール（球1）をキャッチャーへ移動
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: catcherCenterX,
      y: catcherCenterY,
      duration: pitchDuration,
      onComplete: () => {
        // 分身ボールを削除
        this.cleanupBallClones();
        this.handleBallReachedCatcher();
      },
    });
    
    // 球2（分身1）の軌道
    this.tweens.add({
      targets: clone2,
      x: viaX,
      y: viaY2,
      duration: duration2a,
      onComplete: () => {
        this.tweens.add({
          targets: clone2,
          x: catcherCenterX,
          y: endY2, // groundY - 80で終了
          duration: duration2b,
        });
      },
    });
    
    // 球3（分身2）の軌道
    this.tweens.add({
      targets: clone3,
      x: viaX,
      y: viaY3,
      duration: duration3a,
      onComplete: () => {
        this.tweens.add({
          targets: clone3,
          x: catcherCenterX,
          y: endY3, // groundY - 80で終了
          duration: duration3b,
        });
      },
    });

    this.time.delayedCall(pitchDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球5：超速球（シンプルにめっちゃ早い）
  private startKnuckleball5Pitch(catcherCenterX: number, catcherCenterY: number, totalDistance: number): void {
    // 通常の投球と同じ軌道で、速度だけを非常に速くする
    const startX = this.ballStartX;
    const startY = this.ball ? this.ball.y : this.ballStartY;
    
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }
    
    // 超速球の速度（通常の投球と同じ軌道）
    const speed = this.currentPitchSpeed; // 1500（非常に速い）
    const distance = Phaser.Math.Distance.Between(startX, startY, catcherCenterX, catcherCenterY);
    const pitchDuration = (distance / speed) * 1000;
    this.pitchDuration = pitchDuration;
    this.perfectTimingTime = pitchDuration * 0.8;
    
    // ボールをキャッチャーの縦中央に向かって移動（通常の投球と同じ軌道）
    this.ballTween = this.tweens.add({
      targets: this.ball,
      x: catcherCenterX,
      y: catcherCenterY,
      duration: pitchDuration,
      onComplete: () => {
        this.handleBallReachedCatcher();
      },
    });

    this.time.delayedCall(pitchDuration * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });
  }

  // 魔球6：30%付近で停止して回転、その後加速して進む
  private startKnuckleball6Pitch(catcherCenterX: number, catcherCenterY: number, distance: number): void {
    // タイミング開始時刻を記録（投球開始時）
    this.timingStartTime = this.time.now;

    // タイミングバーを表示（投球開始と同時に表示）
    if (this.timingBarBg) this.timingBarBg.setVisible(true);
    if (this.timingBarPointer) this.timingBarPointer.setVisible(true);

    // 投球タイプ表示を非表示にする（投球開始後は非表示）
    if (this.pitchTypeText) {
      this.time.delayedCall(1000, () => {
        if (this.pitchTypeText) {
          this.pitchTypeText.setVisible(false);
        }
      });
    }

    const startX = this.ballStartX;
    // Y座標は通常の投球と同じ（startY (-80) → catcherCenterY）
    const { height } = this.scale;
    const groundHeight = height * 0.3;
    const groundY = height - groundHeight;
    const startY = groundY - 80; // 通常の投球と同じ位置

    const baseSpeed = this.currentPitchSpeed; // 400

    // 40%地点の位置を計算
    const stopPointX = startX + (catcherCenterX - startX) * 0.4;
    const stopPointY = startY + (catcherCenterY - startY) * 0.4;

    // 40%地点までの距離と時間
    const distanceToStop = Phaser.Math.Distance.Between(startX, startY, stopPointX, stopPointY);
    const durationToStop = (distanceToStop / baseSpeed) * 1000;

    // 最初のセグメントでタイミング状態に移行
    this.time.delayedCall(durationToStop * 0.2, () => {
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }
    });

    // 40%地点まで進む
    this.tweens.add({
      targets: this.ball,
      x: stopPointX,
      y: stopPointY,
      duration: durationToStop,
      onComplete: () => {
        if (!this.ball) return;

        // 40%地点で停止し、ボールを回転させる
        this.ball.setPosition(stopPointX, stopPointY);
        
        // ボールをぐるぐる回転させる（連続回転）
        const rotationTween = this.tweens.add({
          targets: this.ball,
          rotation: Math.PI * 2, // 360度回転
          duration: 200, // 1回転の時間
          repeat: -1, // 無限に繰り返す
        });

        // 1.5秒後に回転を停止して加速して進む
        this.time.delayedCall(1500, () => {
          if (!this.ball) return;

          // 回転を停止
          rotationTween.stop();
          this.ball.setRotation(0);

          // 残りの60%を2.5倍の速度で進む
          const remainingDistance = Phaser.Math.Distance.Between(stopPointX, stopPointY, catcherCenterX, catcherCenterY);
          const acceleratedSpeed = baseSpeed * 2.5; // 2.5倍速
          const durationToCatcher = (remainingDistance / acceleratedSpeed) * 1000;

          this.tweens.add({
            targets: this.ball,
            x: catcherCenterX,
            y: catcherCenterY,
            duration: durationToCatcher,
            onComplete: () => {
              this.handleBallReachedCatcher();
            },
          });
        });
      },
    });

    // 総投球時間を計算（タイミング判定用）
    const remainingDistance = Phaser.Math.Distance.Between(stopPointX, stopPointY, catcherCenterX, catcherCenterY);
    const acceleratedSpeed = baseSpeed * 2.5;
    const durationToCatcher = (remainingDistance / acceleratedSpeed) * 1000;
    const totalDuration = durationToStop + 1500 + durationToCatcher; // 40%まで + 停止時間(1.5秒) + 残り60%
    this.pitchDuration = totalDuration;
    this.perfectTimingTime = totalDuration * 0.8;
  }

  // 分身ボールをクリーンアップ
  private cleanupBallClones(): void {
    this.ballClones.forEach(clone => {
      clone.destroy();
    });
    this.ballClones = [];
  }

  private playCatcherMissReaction(pitchType?: string): void {
    if (!this.catcher || !this.catcherScreen) return;

    const missPitchType = pitchType || this.currentPitchType;
    if (missPitchType === 'knuckleball5') {
      const { width } = this.scale;
      const flyDistance = width * 1.5; // 画面幅の1.5倍後ろに飛ばす
      const flyDuration = 1500; // 1500msで飛ばす（目視できる速度）

      // キャッチャーを後ろに回転しながら飛ばす
      this.tweens.add({
        targets: this.catcher,
        x: this.catcherScreen.x + flyDistance,
        rotation: Math.PI * 2, // 2回転（目視できる速度）
        duration: flyDuration,
        ease: 'Power2',
        onComplete: () => {
          // 飛ばした後、500ms後に戻す（次の球を投げる前に戻る）
          this.time.delayedCall(500, () => {
            if (this.catcher && this.catcherScreen) {
              this.tweens.add({
                targets: this.catcher,
                x: this.catcherScreen.x,
                rotation: 0, // 回転をリセット
                duration: 300,
                ease: 'Power2',
              });
            }
          });
        },
      });
      return;
    }

    // 通常の投球の場合、キャッチャーをボールを取った風に少し動かす（前後にブルっと）
    this.tweens.add({
      targets: this.catcher,
      x: this.catcherScreen.x + 5, // 少し前に移動
      duration: 100,
      ease: 'Power2',
      yoyo: true, // 元の位置に戻す
      repeat: 0,
    });
  }

  // ボールがキャッチャーに到達した時の処理（共通化）
  private handleBallReachedCatcher(): void {
    console.log('[handleBallReachedCatcher] ボールがキャッチャーに到達:', {
      gameState: this.gameState,
      isSwingMissed: this.isSwingMissed,
      pendingMissResult: (this as any).pendingMissResult,
      ballExists: !!this.ball,
    });

    // ボールがキャッチャーに到達した時点で、まだスイングしていない場合のみ空振り判定
    // gameStateが'angleBar'になっている場合は、既にスイングして角度フェーズに移行している
    if (this.gameState === 'pitching' || this.gameState === 'timing') {
      console.log('[handleBallReachedCatcher] スイングなし = 見逃しMISS判定');
      // タイミング判定に失敗（スイングしなかった = 空振り）
      // 実際のタイミング差を計算して、LATEかVERY_LATEかを判定
      const currentTime = this.time.now - this.timingStartTime;

      // 投球時間に対するパーセンテージで判定
      const percentage = this.pitchDuration > 0 ? (currentTime / this.pitchDuration) * 100 : 100;

      // タイミングバーのポインターを最終位置に移動（見逃した時の位置を表示）
      if (this.timingBarPointer && this.timingBarBg && this.pitchDuration > 0) {
            // 0% = 左端（ballStartX）、100% = 右端（catcherX）
            // バーの左端から右端まで（0%から100%まで）をマッピング
            const normalizedPercentage = percentage / 100; // 0（0%）〜1（100%）に正規化
            const normalizedDiff = Phaser.Math.Clamp(normalizedPercentage, 0, 1);
            const pointerX = this.timingBarStartX + (normalizedDiff * this.timingBarWidth); // バーの左端から右端まで（0%から100%まで）

            // 最終位置を保存（gameStateが'result'になっても位置を維持するため）
            this.finalTimingPointerX = pointerX;
            this.timingBarPointer.setX(pointerX);

            // ポインターの色をタイミングに応じて変更
            // タイミングバー画像の色に対応
            if (percentage < 70 || percentage >= 93) {
              // 空振り範囲（赤色）- 左赤・右赤ゾーン
              this.timingBarPointer.setFillStyle(0xff0000); // 赤（空振り）
            } else if (percentage >= 81 && percentage < 85) {
              // ベストタイミング（81-85%）- 緑ゾーン
              this.timingBarPointer.setFillStyle(0x00ff00); // 緑
            } else if (percentage >= 76 && percentage < 81) {
              // 早い（76-81%）- 左黄色ゾーン
              this.timingBarPointer.setFillStyle(0xffff00); // 黄
            } else if (percentage >= 70 && percentage < 76) {
              // とても早い（70-76%）- 左オレンジゾーン
              this.timingBarPointer.setFillStyle(0xff8800); // オレンジ
            } else if (percentage >= 85 && percentage < 89) {
              // 遅い（85-89%）- 右黄色ゾーン
              this.timingBarPointer.setFillStyle(0xffff00); // 黄
            } else if (percentage >= 89 && percentage < 93) {
              // とても遅い（89-93%）- 右オレンジゾーン
              this.timingBarPointer.setFillStyle(0xff8800); // オレンジ
            } else {
              // その他（デフォルト）
              this.timingBarPointer.setFillStyle(0xff0000); // 赤
        }
      }

      // ボールがキャッチャーに到達するまでスイングしなかった場合
      // 「とても遅い」を超えた場合として空振り判定
      const timingResult = TimingResult.VERY_LATE;

      console.log('ボール到達時のタイミング判定（スイングなし = 空振り）:', {
        percentage,
        currentTime,
        pitchDuration: this.pitchDuration,
        timingResult,
        gameState: this.gameState,
        willProceedToAngleBar: false,
      });

      // 空振りとして処理（ボールは停止せず、そのまま進む）
      console.log('[handleBallReachedCatcher] 見逃しMISS: handleTimingMissを呼び出し');
      this.handleTimingMiss(timingResult);
      console.log('[handleBallReachedCatcher] 見逃しMISS: handleTimingMiss完了', {
        isSwingMissed: this.isSwingMissed,
        pendingMissResult: (this as any).pendingMissResult,
      });

      // 見逃しMISSの場合、即座に結果を表示する
      // handleTimingMiss()でpendingMissResultが設定されているので、それを取得
      if (this.isSwingMissed && (this as any).pendingMissResult) {
        const pendingResult = (this as any).pendingMissResult;

        console.log('[handleBallReachedCatcher] 見逃しMISS: 結果を表示開始', {
          pendingResult,
        });

        // ゲーム状態を結果に変更（タイミング待ち状態を解除）
        this.gameState = 'result';
        this.tapCount = 0;

        // ボールをキャッチャーと重なる位置に配置して非表示にする
        if (this.ball && this.catcherScreen) {
          const catcherX = this.catcherScreen.x;
          const catcherY = this.catcherScreen.y - 40;

          // ボールのdepthをキャッチャーより下にする
          if (this.catcher) {
            const catcherDepth = this.catcher.depth;
            this.ball.setDepth(catcherDepth - 1);
          }

          // ボールを非表示にする（ミットに入ったように見せる）
          this.ball.setPosition(catcherX, catcherY);
          this.ball.setVisible(false);

          this.playCatcherMissReaction(this.currentPitchType);
        }

        // MISSの場合、ストライクを追加
        if (pendingResult.result === HitResult.MISS) {
          this.strikes++;
          this.updateStrikeDisplay();
          console.log('[handleBallReachedCatcher] 見逃しMISS: ストライク追加', {
            strikes: this.strikes,
          });
          
          // 3ストライクでゲームオーバー
          if (this.strikes >= 3) {
            console.log('[handleBallReachedCatcher] 3ストライク: ゲームオーバー');
            // 結果を表示してからゲームオーバー画面へ
            const pitchResult: PitchResult = {
              result: pendingResult.result,
              distance: 0,
              score: 0,
              timingResult: pendingResult.timingResult,
            };
            this.pitchResults.push(pitchResult);
            this.showResult(pitchResult);
            // ゲームオーバー処理はshowResultの後で実行される
            return;
          }
        }

        // 結果を表示
        const pitchResult: PitchResult = {
          result: pendingResult.result,
          distance: 0,
          score: 0,
          timingResult: pendingResult.timingResult,
        };
        console.log('[handleBallReachedCatcher] 見逃しMISS: pitchResultを作成', {
          pitchResult,
        });
        this.pitchResults.push(pitchResult);
        console.log('[handleBallReachedCatcher] 見逃しMISS: showResultを呼び出し', {
          pitchResultsLength: this.pitchResults.length,
        });
        this.showResult(pitchResult);

        // 保存した結果をクリア
        (this as any).pendingMissResult = null;
      }
    } else if (this.isSwingMissed && (this as any).pendingMissResult) {
          console.log('[startNextPitch onComplete] 空振り判定後、ボールがキャッチャーに到達（条件チェック通過）');
          // 空振り判定後、ボールがキャッチャーに到達した時（見逃しMISSの場合）
          const pendingResult = (this as any).pendingMissResult;

          console.log('[startNextPitch onComplete] 空振り判定後、ボールがキャッチャーに到達（見逃しMISS）:', {
            gameState: this.gameState,
            isSwingMissed: this.isSwingMissed,
            pendingResult,
          });

          // ゲーム状態を結果に変更（タイミング待ち状態を解除）
          this.gameState = 'result';
          this.tapCount = 0;

          // ボールをキャッチャーと重なる位置に配置して非表示にする
          if (this.ball && this.catcherScreen) {
            const catcherX = this.catcherScreen.x;
            const catcherY = this.catcherScreen.y - 40;

            // ボールのdepthをキャッチャーより下にする
            if (this.catcher) {
              const catcherDepth = this.catcher.depth;
              this.ball.setDepth(catcherDepth - 1);
            }

            // ボールを非表示にする（ミットに入ったように見せる）
            this.ball.setPosition(catcherX, catcherY);
            this.ball.setVisible(false);

            this.playCatcherMissReaction((pendingResult as any).pitchType);
          }

          // MISSの場合、ストライクを追加
          if (pendingResult.result === HitResult.MISS) {
            this.strikes++;
            this.updateStrikeDisplay();
            console.log('[startNextPitch onComplete] 見逃しMISS: ストライク追加', {
              strikes: this.strikes,
            });
            
            // 3ストライクでゲームオーバー
            if (this.strikes >= 3) {
              console.log('[startNextPitch onComplete] 3ストライク: ゲームオーバー');
            }
          }

          // 結果を表示
          const pitchResult: PitchResult = {
            result: pendingResult.result,
            distance: 0,
            score: 0,
            timingResult: pendingResult.timingResult,
          };
          console.log('[startNextPitch onComplete] 見逃しMISS: pitchResultを作成', {
            pitchResult,
          });
          this.pitchResults.push(pitchResult);
          console.log('[startNextPitch onComplete] 見逃しMISS: showResultを呼び出し', {
            pitchResultsLength: this.pitchResults.length,
          });
          this.showResult(pitchResult);

      // 保存した結果をクリア
      (this as any).pendingMissResult = null;
    } else {
      console.log('[handleBallReachedCatcher] ボール到達時、既に角度フェーズに移行済み:', {
        gameState: this.gameState,
        isSwingMissed: this.isSwingMissed,
        hasPendingResult: !!(this as any).pendingMissResult,
      });
    }
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {

    // 一時停止中はスイング判定をスキップ
    if (this.isPaused) {
      return;
    }

    // waiting状態の場合はスイング判定をスキップ（PlayBallボタンが表示されている状態）
    if (this.gameState === 'waiting') {
      return;
    }

    // 一時停止ボタンまたはサウンドボタンがクリックされた場合はスイング判定をスキップ
    // ボタンの位置とポインターの位置を比較
    const pointerX = pointer.x;
    const pointerY = pointer.y;

    if (this.pauseButton && this.pauseButton.visible) {
      const pauseBounds = this.pauseButton.getBounds();
      if (pointerX >= pauseBounds.x && pointerX <= pauseBounds.x + pauseBounds.width &&
        pointerY >= pauseBounds.y && pointerY <= pauseBounds.y + pauseBounds.height) {
        return; // 一時停止ボタンがクリックされた場合はスイング判定をスキップ
      }
    }

    if (this.soundButton && this.soundButton.visible) {
      const soundBounds = this.soundButton.getBounds();
      if (pointerX >= soundBounds.x && pointerX <= soundBounds.x + soundBounds.width &&
        pointerY >= soundBounds.y && pointerY <= soundBounds.y + soundBounds.height) {
        return; // サウンドボタンがクリックされた場合はスイング判定をスキップ
      }
    }

    // PlayBallボタンが表示されている場合はスイング判定をスキップ
    if (this.playBallButton && this.playBallButton.visible) {
      const playBallBounds = this.playBallButton.getBounds();
      if (pointerX >= playBallBounds.x && pointerX <= playBallBounds.x + playBallBounds.width &&
        pointerY >= playBallBounds.y && pointerY <= playBallBounds.y + playBallBounds.height) {
        return; // PlayBallボタンがクリックされた場合はスイング判定をスキップ
      }
    }

    if (this.gameState === 'pitching' || this.gameState === 'timing') {
      // 空振り判定後はスイングタップ無効（2回スイングできない）
      if (this.isSwingMissed) {
        console.log('[onPointerDown] 空振り判定後、スイングタップ無効');
        return;
      }

      console.log('[onPointerDown] スイング判定開始:', {
        gameState: this.gameState,
        ballExists: !!this.ball,
        ballX: this.ball?.x,
        ballY: this.ball?.y,
      });

      // スイング開始時点でボールを止め、その位置を記録
      if (this.ball) {
        this.swingBallScreenX = this.ball.x;
        this.swingBallScreenY = this.ball.y;
        this.hasSwingBallPos = true;

        // 投球Tweenを止める（その場で停止）
        this.tweens.killTweensOf(this.ball);
        
        // 分身ボールを削除（スイング時は本物だけ表示）
        this.cleanupBallClones();

        // スイング開始時にカーソルを即座にボール位置へ合わせる（ラグ防止）
        if (this.timingBarPointer) {
          const catcherCenterX = this.catcherScreen ? this.catcherScreen.x : this.ballTargetX;
          const startX = this.ballStartX;
          const totalDist = catcherCenterX - startX;
          const ratio = totalDist !== 0 ? (this.swingBallScreenX - startX) / totalDist : 0;
          const clampedRatio = Phaser.Math.Clamp(ratio, 0, 1);
          const pointerX = this.timingBarStartX + (clampedRatio * this.timingBarWidth);
          this.timingBarPointer.setX(pointerX);
        }
      }

      // タイミング判定（pitching状態でも判定可能にする）
      // gameStateがpitchingの場合は、timingに移行してから判定
      if (this.gameState === 'pitching') {
        this.gameState = 'timing';
      }

      // タイミング判定
      const timingResult = this.checkTiming();

      // 空振り判定の場合は、ボールのtweenを再開する必要がある
      if (this.isSwingMissed && this.ball && timingResult === null) {
        // 空振り判定の場合、ボールのtweenを再開する
        // ボールをキャッチャーまで進める
        const catcherCenterX = this.catcherScreen ? this.catcherScreen.x : this.ballTargetX;
        const catcherCenterY = this.catcherScreen ? this.catcherScreen.y - 45 : this.ballTargetY;
        const remainingDistance = Phaser.Math.Distance.Between(
          this.ball.x,
          this.ball.y,
          catcherCenterX,
          catcherCenterY
        );
        const remainingTime = Math.max(100, (remainingDistance / this.currentPitchSpeed) * 1000);

        console.log('空振り判定: ボールのtweenを再開', {
          ballX: this.ball.x,
          ballY: this.ball.y,
          catcherX: catcherCenterX,
          catcherY: catcherCenterY,
          remainingDistance,
          remainingTime,
        });

        // ボールをキャッチャーまで進める
        // このtweenが完了した時に、元のonCompleteコールバックが呼ばれるようにする必要がある
        // しかし、元のtweenは既にkillTweensOfで停止されているので、
        // ここで新しいtweenを作成し、完了時に結果を表示する
        console.log('[onPointerDown] 空振り判定: ボールのtweenを再開', {
          ballX: this.ball.x,
          ballY: this.ball.y,
          catcherX: catcherCenterX,
          catcherY: catcherCenterY,
          remainingDistance,
          remainingTime,
          isSwingMissed: this.isSwingMissed,
          pendingMissResult: (this as any).pendingMissResult,
        });

        this.tweens.add({
          targets: this.ball,
          x: catcherCenterX,
          y: catcherCenterY,
          duration: remainingTime,
          onComplete: () => {
            console.log('[onPointerDown] 空振り判定: ボールがキャッチャーに到達（再開tween完了）', {
              gameState: this.gameState,
              isSwingMissed: this.isSwingMissed,
              pendingMissResult: (this as any).pendingMissResult,
              ballExists: !!this.ball,
            });

            // 空振り判定後、ボールがキャッチャーに到達した時
            if (this.isSwingMissed && (this as any).pendingMissResult) {
              const pendingResult = (this as any).pendingMissResult;

              console.log('[onPointerDown] 空振り判定: 結果を表示開始', {
                pendingResult,
              });

              // ゲーム状態を結果に変更（タイミング待ち状態を解除）
              this.gameState = 'result';
              this.tapCount = 0;

              // ボールをキャッチャーと重なる位置に配置して非表示にする
              if (this.ball && this.catcherScreen) {
                const catcherX = this.catcherScreen.x;
                const catcherY = this.catcherScreen.y - 40;

                // ボールのdepthをキャッチャーより下にする
                if (this.catcher) {
                  const catcherDepth = this.catcher.depth;
                  this.ball.setDepth(catcherDepth - 1);
                }

                // ボールを非表示にする（ミットに入ったように見せる）
                this.ball.setPosition(catcherX, catcherY);
                this.ball.setVisible(false);

                // キャッチャーの反応（超速球の場合は飛んでいく）
                this.playCatcherMissReaction((pendingResult as any).pitchType);
              }

              // MISSの場合、ストライクを追加
              if (pendingResult.result === HitResult.MISS) {
                this.strikes++;
                this.updateStrikeDisplay();
                console.log('[onPointerDown] 空振りMISS: ストライク追加', {
                  strikes: this.strikes,
                });
                
                // 3ストライクでゲームオーバー
                if (this.strikes >= 3) {
                  console.log('[onPointerDown] 3ストライク: ゲームオーバー');
                }
              }

              // 結果を表示
              const pitchResult: PitchResult = {
                result: pendingResult.result,
                distance: 0,
                score: 0,
                timingResult: pendingResult.timingResult,
              };
              console.log('[onPointerDown] 空振り判定: pitchResultを作成', {
                pitchResult,
              });
              this.pitchResults.push(pitchResult);
              console.log('[onPointerDown] 空振り判定: showResultを呼び出し', {
                pitchResultsLength: this.pitchResults.length,
              });
              this.showResult(pitchResult);

              // 保存した結果をクリア
              (this as any).pendingMissResult = null;
            } else {
              console.log('[onPointerDown] 空振り判定: 条件が満たされていません', {
                isSwingMissed: this.isSwingMissed,
                hasPendingResult: !!(this as any).pendingMissResult,
              });
            }
          },
        });
      } else {
        // タイミング判定が成功した場合（角度フェーズに移行）
        console.log('[onPointerDown] タイミング判定成功、角度フェーズに移行:', {
          timingResult,
        });
      }
    } else {
      console.log('[onPointerDown] ゲーム状態が不正:', {
        gameState: this.gameState,
        expectedStates: ['pitching', 'timing'],
      });
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {

    // 一時停止中はスイング判定をスキップ
    if (this.isPaused) {
      return;
    }

    if (this.gameState === 'angleBar') {
      // 離した時：カーソルを現在の位置で停止して角度決定
      this.isCursorMoving = false; // カーソルを停止

      // 現在のカーソル位置を確定（update()で更新された位置を使用）
      // 角度は既にupdate()で計算されているので、そのまま使用

      this.determineHit();
    }
  }

  private checkTiming(): TimingResult | null {
    const currentTime = this.time.now - this.timingStartTime;

    // 判定用のパーセンテージを計算
    // 魔球の場合、時間とボール位置が一致しないためボール位置を優先
    let percentage: number;
    let timingRatio: number | null = null;
    if (this.hasSwingBallPos) {
      const catcherCenterX = this.catcherScreen ? this.catcherScreen.x : this.ballTargetX;
      const startX = this.ballStartX;
      const totalDist = catcherCenterX - startX;
      const ratio = totalDist !== 0 ? (this.swingBallScreenX - startX) / totalDist : 0;
      timingRatio = Phaser.Math.Clamp(ratio, 0, 1);
      percentage = timingRatio * 100;
    } else {
      // 投球時間に対するパーセンテージで判定
      // 0 = 投球開始、100 = キャッチャー到達
      percentage = (currentTime / this.pitchDuration) * 100;
    }

    let timingResult: TimingResult;
    const normalizedRatio = Phaser.Math.Clamp(percentage / 100, 0, 1);
    const evaluatedTiming = this.evaluateTimingFromBarRatio(timingRatio !== null ? timingRatio : normalizedRatio);

    // 5段階のタイミング判定（パーセンテージベース）
    // キャッチャーミットは100%（タイミングメーターの右端）
    // タイミングバー画像の色に対応:
    // 0-70%: 空振り/MISS（左赤ゾーン - 角度フェーズに移行しない）
    // とても早い: 70-76%（6%）- 左オレンジ
    // 早い: 76-81%（5%）- 左黄色
    // ベスト: 81-85%（4%）- 緑
    // 遅い: 85-89%（4%）- 右黄色
    // とても遅い: 89-93%（4%）- 右オレンジ
    // 93-100%: 空振り/MISS（右赤ゾーン - 角度フェーズに移行しない）
    console.log('checkTimingが呼び出されました:', {
      currentTime,
      pitchDuration: this.pitchDuration,
      percentage,
      timingStartTime: this.timingStartTime,
    });

    // 空振り判定（画像ベース優先、なければパーセンテージ）
    if (evaluatedTiming?.isMiss || (!evaluatedTiming && (percentage < 70 || percentage >= 93))) {
      // 空振りとして処理（角度フェーズに移行しない）
      const missTimingResult = evaluatedTiming?.isMiss
        ? evaluatedTiming.missTimingResult
        : (percentage < 70 ? TimingResult.VERY_EARLY : TimingResult.VERY_LATE);
      console.log('空振り判定:', {
        percentage,
        missTimingResult,
        willProceedToAngleBar: false,
      });

      // ボールのtweenを停止しない（速度を維持）
      // onPointerDown()で停止された場合は、再開する必要はない（空振り判定なので）

      this.handleTimingMiss(missTimingResult);
      return null; // 角度フェーズに移行しない
    }

    // タイミング判定（画像ベース優先、なければパーセンテージ）
    if (evaluatedTiming && !evaluatedTiming.isMiss) {
      timingResult = evaluatedTiming.timingResult;
    } else {
      // タイミングバー画像の色に対応
      if (percentage >= 81 && percentage < 85) {
        // ベストタイミング（81-85%）- 緑ゾーン
        timingResult = TimingResult.BEST;
      } else if (percentage >= 76 && percentage < 81) {
        // 早い（76-81%）- 左黄色ゾーン
        timingResult = TimingResult.EARLY;
      } else if (percentage >= 70 && percentage < 76) {
        // とても早い（70-76%）- 左オレンジゾーン
        timingResult = TimingResult.VERY_EARLY;
      } else if (percentage >= 85 && percentage < 89) {
        // 遅い（85-89%）- 右黄色ゾーン
        timingResult = TimingResult.LATE;
      } else if (percentage >= 89 && percentage < 93) {
        // とても遅い（89-93%）- 右オレンジゾーン
        timingResult = TimingResult.VERY_LATE;
      } else {
        // 念のため、範囲外の場合は空振りとして処理
        const missTimingResult = percentage < 70 ? TimingResult.VERY_EARLY : TimingResult.VERY_LATE;
        console.log('範囲外判定（空振り）:', {
          percentage,
          missTimingResult,
        });
        this.handleTimingMiss(missTimingResult);
        return null;
      }
    }

    // デバッグログ（タイミング判定の確認用）
    // percentageからdiffを計算（50%を基準とした差分）
    const diff = currentTime - (this.pitchDuration * 0.5); // 50%を基準とした差分
    console.log('タイミング判定:', {
      currentTime,
      pitchDuration: this.pitchDuration,
      percentage,
      diff,
      timingResult,
    });

    // スイングタイミングの結果を表示（横長のバーで表示）
    const timingLabels: { [key: string]: string } = {
      [TimingResult.VERY_EARLY]: 'とても早い',
      [TimingResult.EARLY]: '早い',
      [TimingResult.BEST]: 'ベスト',
      [TimingResult.LATE]: '遅い',
      [TimingResult.VERY_LATE]: 'とても遅い',
    };
    const timingLabel = timingLabels[timingResult] || timingResult;
    const timingColor = timingResult === TimingResult.BEST ? '#00ff00' : '#ffff00';
    this.timingText.setText(`タイミング: ${timingLabel}`).setColor(timingColor).setVisible(true);

    // タイミングバーを表示
    const { height } = this.scale;
    // groundYはcreate()で定義済み（height * 0.7）
    const timingBarY = height * 0.7 + 60; // バーのY座標（ピッチャーとバッターの少し下）

    // バーの背景と各範囲セグメントを表示
    this.timingBarBg.setVisible(true);
    // ベスト帯は画像に含まれているため不要
    // セグメントは画像に含まれているため不要

    // タイミング位置をバー上にマッピング（ボール位置ベース）
    // 魔球の場合、時間とボール位置が一致しないため、ボール位置から計算する
    let pointerX: number;
    if (this.hasSwingBallPos) {
      // スイング開始時のボール位置から計算（上で算出したratioを利用）
      const clampedRatio = timingRatio !== null ? timingRatio : 0;
      pointerX = this.timingBarStartX + (clampedRatio * this.timingBarWidth);
    } else {
      // スイングしていない場合は時間ベースで計算（通常はこのケースには来ない）
      const normalizedPercentage = percentage / 100;
      const normalizedDiff = Phaser.Math.Clamp(normalizedPercentage, 0, 1);
      pointerX = this.timingBarStartX + (normalizedDiff * this.timingBarWidth);
    }

    this.timingBarPointer.setX(pointerX);
    this.timingBarPointer.setVisible(true);

    // ポインターの色をタイミングに応じて変更
    // タイミングバー画像の色に対応
    if (evaluatedTiming?.isMiss || (!evaluatedTiming && (percentage < 70 || percentage >= 93))) {
      // 空振り範囲（赤色）- 左赤・右赤ゾーン
      this.timingBarPointer.setFillStyle(0xff0000); // 赤（空振り）
    } else if (timingResult === TimingResult.BEST) {
      // ベスト（81-85%）- 緑ゾーン
      this.timingBarPointer.setFillStyle(0x00ff00); // 緑
    } else if (timingResult === TimingResult.EARLY || timingResult === TimingResult.LATE) {
      // 早い/遅い（76-81%/85-89%）- 黄色ゾーン
      this.timingBarPointer.setFillStyle(0xffff00); // 黄
    } else if (timingResult === TimingResult.VERY_EARLY || timingResult === TimingResult.VERY_LATE) {
      // とても早い/とても遅い（70-76%/89-93%）- オレンジゾーン
      this.timingBarPointer.setFillStyle(0xff8800); // オレンジ
    } else {
      // その他（デフォルト）
      this.timingBarPointer.setFillStyle(0xff0000); // 赤
    }

    // デバッグ情報を表示
    this.debugText.setText(
      `タイミング: ${timingResult} (${percentage.toFixed(1)}%, 最適: 83%)`
    );

    console.log('タイミングチェック:', {
      timingResult,
      percentage,
      diff,
      currentTime,
      pitchDuration: this.pitchDuration,
    });

    // タイミング判定結果に基づいて処理を分岐
    // すべてのタイミング結果（VERY_EARLY、EARLY、BEST、LATE、VERY_LATE）で角度バー操作へ移行
    // スイングした場合はすべて打球を飛ばす
    console.log('タイミング判定結果の分岐:', {
      timingResult,
      diff,
      isBEST: timingResult === TimingResult.BEST,
      isEARLY: timingResult === TimingResult.EARLY,
      isLATE: timingResult === TimingResult.LATE,
      isVERY_LATE: timingResult === TimingResult.VERY_LATE,
      isVERY_EARLY: timingResult === TimingResult.VERY_EARLY,
      willProceedToAngleBar: true, // すべてのタイミング結果で角度バー操作へ移行
    });

    // すべてのタイミング結果で角度バー操作へ移行（スイングした場合はすべて打球を飛ばす）
    console.log('タイミング成功！角度バーを開始します...', {
      timingResult,
      diff,
      willProceed: true,
    });
    this.startAngleBar(timingResult);
    return timingResult;
  }

  private handleTimingMiss(timingResult: TimingResult): void {
    console.log('[handleTimingMiss] 開始:', {
      timingResult,
      gameState: this.gameState,
      ballExists: !!this.ball,
      ballX: this.ball?.x,
      ballY: this.ball?.y,
    });

    // 空振りフラグを設定（その1打席でスイングタップ無効）
    this.isSwingMissed = true;

    // 空振りゾーンの時のみスイング音を再生（スイングした場合のみ）
    if (this.hasSwingBallPos) {
      this.sound.play('swing', { volume: 0.5 });
    }

    // 最終位置をリセット（次の投球で再計算されるように）
    this.finalTimingPointerX = null;

    // ボールは停止しない（速度を維持）
    // ボールのtweenは停止せず、そのままキャッチャーまで進む

    // バッターのスイングアニメーションを全フレーム（0-5）再生（VERY_EARLYより早い、またはVERY_LATEより遅い場合）
    // 見逃し（スイングしない）の場合は再生しない
    if (this.hasSwingBallPos && this.batter && this.anims.exists('batter_swing')) {
      console.log('[Batter Animation] 空振り - 全フレーム（0-5）再生開始');

      // 既存のイベントリスナーをクリア（重複防止）
      this.batter.off('animationupdate');
      this.batter.off('animationcomplete');

      // アニメーションを停止してから再生（最初から再生）
      if (this.batter.anims.isPlaying) {
        this.batter.anims.stop();
      }

      // 全フレーム（0-5）を再生するアニメーションを作成（既存のbatter_swingを使用）
      if (!this.anims.exists('batter_swing_full')) {
        const frames = this.anims.generateFrameNumbers('batter_ss', { start: 0, end: 5 });
        this.anims.create({
          key: 'batter_swing_full',
          frames: frames, // 0-5フレーム（全フレーム）
          frameRate: 15,
          repeat: 0, // 1回のみ再生
        });
      }

      // アニメーションを再生（全フレーム）
      this.batter.play('batter_swing_full');
    } else if (!this.hasSwingBallPos) {
      console.log('[Batter Animation] 見逃しMISS - スイングアニメーションは再生しない');
    }

    let result: HitResult;
    if (this.config.earlyIsFoul && timingResult === TimingResult.EARLY) {
      result = HitResult.FOUL;
    } else {
      result = HitResult.MISS;
    }

    // ボールがキャッチャーに到達した時に結果を表示するため、結果を保存
    (this as any).pendingMissResult = {
      result,
      timingResult,
      pitchType: this.currentPitchType, // 投球タイプも保存（超速球判定用）
    };

    console.log('[handleTimingMiss] 完了:', {
      timingResult,
      result,
      isSwingMissed: this.isSwingMissed,
      pendingMissResult: (this as any).pendingMissResult,
      willWaitForBallToReachCatcher: true,
    });
  }

  private startAngleBar(timingResult: TimingResult): void {
    console.log('startAngleBarが呼び出されました');

    // バッターのスイングアニメーションを開始（1度目のタップ：0-3フレームまで再生して停止）
    if (this.batter && this.anims.exists('batter_swing')) {
      console.log('[Batter Animation] スイングアニメーション開始（0-3フレーム）');

      // 既存のイベントリスナーをクリア（重複防止）
      this.batter.off('animationupdate');
      this.batter.off('animationcomplete');

      // アニメーションを停止してから再生（最初から再生）
      if (this.batter.anims.isPlaying) {
        this.batter.anims.stop();
      }

      // フレーム0-3まで再生して停止するアニメーションを作成
      const frames = this.anims.generateFrameNumbers('batter_ss', { start: 0, end: 3 });
      if (!this.anims.exists('batter_swing_part1')) {
        this.anims.create({
          key: 'batter_swing_part1',
          frames: frames, // 0-3フレーム
          frameRate: 15,
          repeat: 0,
        });
      }

      // フレーム3で停止するようにイベントリスナーを設定
      this.batter.on('animationupdate', (animation: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        if (frame.index >= 3) {
          // フレーム3に到達したら停止
          this.batter.anims.stop();
          this.batter.setFrame(3);
          console.log('[Batter Animation] フレーム3で停止');
        }
      });

      // アニメーションを再生（0-3フレーム）
      this.batter.play('batter_swing_part1');
    }

    // ボールの移動を停止（スイング開始位置で停止）
    if (this.ball) {
      this.tweens.killTweensOf(this.ball);

      // 念のため、保存してある"スイング開始位置"に固定
      if (this.hasSwingBallPos) {
        this.ball.setPosition(this.swingBallScreenX, this.swingBallScreenY);
      }

      this.ball.setVisible(true);
    }

    this.gameState = 'angleBar';
    this.angleBarSpeed = this.config.barSpeed0;
    this.angleBarTime = 0;
    this.currentAngle = this.config.barMinAngle; // 0度からスタート（角度メーター上：0度=下=水平、実際の0度）

    // カーソルを動かし始める
    this.isCursorMoving = true;

    // ポインターを確実に表示（初期位置は0度の位置=下=実際の0度=水平）
    if (this.angleMeterPointer) {
      const initialAngle = this.config.barMinAngle; // 0度からスタート（下から、実際の0度=水平）
      const quarterAngle = this.mapAngleToQuarterCircle(initialAngle);
      const rad = Phaser.Math.DegToRad(quarterAngle);
      const pointerLength = this.angleMeterRadius * 0.7; // ポインターの長さ（半径の70%）
      const endX = this.angleMeterOriginX + Math.cos(rad) * pointerLength;
      const endY = this.angleMeterOriginY - Math.sin(rad) * pointerLength; // sinの符号を反転（270度=下方向にするため）
      this.angleMeterPointer.setTo(
        this.angleMeterOriginX, this.angleMeterOriginY, // 原点
        endX, endY // 終点
      );
      this.angleMeterPointer.setVisible(true);
      this.angleMeterPointer.setAlpha(1.0);
    }

    // デバッグ情報を更新
    this.debugText.setText(`タイミング成功！角度を設定するために赤いゾーンで離してください (${timingResult})`);

    // タイミング結果を保存（後で飛距離計算に使用）
    (this as any).lastTimingResult = timingResult;
  }

  private pauseGame(): void {
    if (this.isPaused) {
      return; // 既に一時停止中
    }

    this.isPaused = true;

    // 一時停止：すべてのアニメーションとタイマーを停止
    this.tweens.pauseAll();
    this.time.paused = true;

    // Matter.js物理エンジンの更新を停止
    if (this.matter && this.matter.world && this.matter.world.engine) {
      const engine = this.matter.world.engine;
      // エンジンのタイムスケールを0にして停止
      (engine as any).timing.timeScale = 0;
      console.log('Matter.js物理エンジンを停止しました');
    }

    // 暗いオーバーレイを表示
    if (this.pausedOverlay) {
      this.pausedOverlay.setVisible(true);
    }

    // 画面中央に「一時停止」テキストを表示
    if (this.pausedText) {
      this.pausedText.setVisible(true);
    }

    // 一時停止ボタンの画像をプレイボタンに切り替え
    if (this.pauseButton) {
      this.pauseButton.setTexture('play');
    }

    console.log('ゲームを一時停止しました', {
      isPaused: this.isPaused,
      pauseButtonExists: !!this.pauseButton,
      pausedTextExists: !!this.pausedText,
      hitBallBodyExists: !!this.hitBallBody,
    });
  }

  private resumeGame(): void {
    if (!this.isPaused) {
      return; // 既に実行中
    }

    this.isPaused = false;

    // 再開：すべてのアニメーションとタイマーを再開
    this.tweens.resumeAll();
    this.time.paused = false;

    // Matter.js物理エンジンの更新を再開
    if (this.matter && this.matter.world && this.matter.world.engine) {
      const engine = this.matter.world.engine;
      // エンジンのタイムスケールを1に戻して再開
      (engine as any).timing.timeScale = 1;
      console.log('Matter.js物理エンジンを再開しました');
    }

    // 暗いオーバーレイを非表示
    if (this.pausedOverlay) {
      this.pausedOverlay.setVisible(false);
    }

    // 「一時停止」テキストを非表示
    if (this.pausedText) {
      this.pausedText.setVisible(false);
    }

    // 一時停止ボタンの画像を停止ボタンに戻す
    if (this.pauseButton) {
      this.pauseButton.setTexture('stop');
    }

    console.log('ゲームを再開しました');
  }

  private toggleSound(): void {
    // サウンドON/OFFを切り替え
    this.isSoundOn = !this.isSoundOn;

    // サウンドマネージャーの音量を設定
    if (this.isSoundOn) {
      this.sound.setVolume(1.0);
      // ボタン画像をONに切り替え
      if (this.soundButton) {
        this.soundButton.setTexture('sounds_on');
      }
    } else {
      this.sound.setVolume(0);
      // ボタン画像をOFFに切り替え
      if (this.soundButton) {
        this.soundButton.setTexture('sounds_off');
      }
    }

    console.log('サウンドを', this.isSoundOn ? 'ON' : 'OFF', 'にしました');
  }

  update(): void {
    // 一時停止中は更新しない
    if (this.isPaused) {
      return;
    }

    // 物理ボディと見た目を同期
    if (this.hitBallBody && this.hitBall) {
      // 物理ボディの位置を取得（ワールド座標）
      const ballWorldX = this.hitBallBody.x;
      const ballWorldY = this.hitBallBody.y;

      // 低い弾道でフェンスをすり抜けるケースのフォールバック対策
      if (this.fenceBody && this.hitBallBody.body && !this.fenceHitSoundPlayed) {
        const body = this.hitBallBody.body as MatterJS.BodyType;
        const velocity = (body as any).velocity;
        const fenceBounds = (this.fenceBody as any).bounds;
        const ballRadius = (body as any).circleRadius || 12;
        const crossedFence =
          this.prevHitBallWorldX !== null &&
          this.prevHitBallWorldX >= fenceBounds.min.x &&
          ballWorldX < fenceBounds.min.x;
        const passedThrough =
          velocity?.x < 0 &&
          crossedFence &&
          ballWorldX < fenceBounds.min.x &&
          ballWorldY > fenceBounds.min.y &&
          ballWorldY < fenceBounds.max.y;

        if (passedThrough) {
          // フェンス前面で止めて、Y方向の運動は維持する
          this.hitBallBody.setPosition(fenceBounds.min.x + ballRadius, ballWorldY);
          this.hitBallBody.setVelocity(0, velocity?.y ?? 0);
          if (!this.fenceHitSoundPlayed) {
            this.sound.play('fence', { volume: 0.5 });
            this.fenceHitSoundPlayed = true;
          }
        }
      }

      this.prevHitBallWorldX = ballWorldX;
      this.prevHitBallWorldY = ballWorldY;


      // 物理ボディの速度を確認（最初の数フレームのみ）
      if (!(this as any).ballDebugCount) {
        (this as any).ballDebugCount = 0;
      }
      if ((this as any).ballDebugCount < 10 && this.hitBallBody.body) {
        const body = this.hitBallBody.body as MatterJS.BodyType;
        const velocity = (body as any).velocity;
        console.log('ボールの位置・速度確認:', {
          frame: (this as any).ballDebugCount,
          x: ballWorldX,
          y: ballWorldY,
          velocityX: velocity?.x,
          velocityY: velocity?.y,
          speed: velocity ? Math.sqrt(velocity.x ** 2 + velocity.y ** 2) : 0,
        });
        (this as any).ballDebugCount++;
      }

      // 見た目を物理ボディと同じ位置に設定（ワールド座標）
      this.hitBall.x = ballWorldX;
      this.hitBall.y = ballWorldY;
      // 回転も同期（Phaserはラジアン）
      this.hitBall.rotation = this.hitBallBody.rotation;
      // 深度を確実に維持（バッターより前面に）
      if (this.hitBall.depth < 200) {
        this.hitBall.setDepth(200);
      }
      if (this.hitBallBody.depth < 200) {
        this.hitBallBody.setDepth(200);
      }

      // 打球の速度を表示（km/hに変換、表示上の速度を調整）
      if (this.hitBallBody.body) {
        // 表示上の速度を計算（ベストタイミング＋ベスト角度を190km/hベースに）
        const maxSpeedKmh = 190;
        const bestAngle = 27.5;
        const angle = (this as any).flyBallAngle || 0;
        const angleDiff = Math.abs(angle - bestAngle);
        const timingResult = (this as any).flyBallTimingResult;

        // タイミング係数（ベストが1.0、それ以外は下がる）
        let timingMultiplier = 1.0;
        if (timingResult === TimingResult.BEST) {
          timingMultiplier = 1.0; // ベストは100%
        } else if (timingResult === TimingResult.EARLY || timingResult === TimingResult.LATE) {
          timingMultiplier = 0.85; // 早い/遅いは85%
        } else if (timingResult === TimingResult.VERY_EARLY || timingResult === TimingResult.VERY_LATE) {
          timingMultiplier = 0.7; // とても早い/とても遅いは70%
        }

        // 角度係数（30度が1.0、それ以外は下がる）
        let angleMultiplier = 1.0;
        if (angleDiff <= 2.5) {
          // 27.5度±2.5度以内：100%
          angleMultiplier = 1.0;
        } else if (angleDiff <= 5.0) {
          // 27.5度±5度以内：90%
          angleMultiplier = 0.9;
        } else if (angleDiff <= 10.0) {
          // 27.5度±10度以内：80%
          angleMultiplier = 0.8;
        } else if (angleDiff <= 20.0) {
          // 27.5度±20度以内：70%
          angleMultiplier = 0.7;
        } else {
          // それ以外：60%
          angleMultiplier = 0.6;
        }

        // ベストタイミング＋ベスト角度の時にたまに最大190km/hを表示（30%の確率）
        // ただし、毎フレーム変わるのを防ぐため、一度計算したら保存する
        if (!(this as any).displaySpeedKmh) {
          const isBestTimingAndAngle = timingResult === TimingResult.BEST && angleDiff <= 2.5;
          if (isBestTimingAndAngle && Math.random() < 0.3) {
            // 30%の確率で最大190km/h
            (this as any).displaySpeedKmh = maxSpeedKmh;
          } else {
            // それ以外は係数を掛けて速度を落とす
            (this as any).displaySpeedKmh = maxSpeedKmh * timingMultiplier * angleMultiplier;
          }
        }

        // リアルタイムで飛距離を計算（実際の物理的な飛距離を基準に）
        const startX = (this as any).flyBallStartX;
        const startY = (this as any).flyBallStartY;

        if (startX !== undefined && startY !== undefined) {
          // 水平方向の距離のみを計算（ボールは左方向に飛ぶ）
          const currentDistancePx = Math.abs(ballWorldX - startX);
          const hrDistanceM = (this as any).hrDistanceM; // 計算された距離（メートル）
          const actualRangePx = (this as any).actualRangePx; // 実際の物理的な飛距離（ピクセル）

          if (hrDistanceM !== undefined && hrDistanceM > 0 && actualRangePx !== undefined && actualRangePx > 0) {
            let displayDistanceM: number;

            // ボールが動いている限り、実際の移動距離を表示し続ける
            // actualRangePx（実際の物理的な飛距離）をメートルに変換するための換算率を計算
            // actualRangePxがhrDistanceMに対応するので、actualRangePx / hrDistanceMが換算率
            const pixelsPerMeterForActual = actualRangePx / hrDistanceM;
            // currentDistancePxを直接メートルに変換（ボールの動きに合わせて加算）
            const rawDisplayDistanceM = currentDistancePx / pixelsPerMeterForActual;

            // 距離の更新をスムーズにするため、前回の値との線形補間を使用
            const previousDisplayDistanceM = (this as any).previousDisplayDistanceM;
            if (previousDisplayDistanceM !== undefined && !this.ballStopped) {
              // 前回の値との線形補間（0.3の重みで前回の値、0.7の重みで新しい値）
              displayDistanceM = previousDisplayDistanceM * 0.3 + rawDisplayDistanceM * 0.7;
            } else {
              displayDistanceM = rawDisplayDistanceM;
            }

            // ボールが動いている間は実際の移動距離を表示し続ける
            // hrDistanceMで制限しない（ボールが目標距離より遠くまで飛ぶ可能性があるため）

            // 前回の値を保存（次回の補間用）
            (this as any).previousDisplayDistanceM = displayDistanceM;

            // 打球速度と飛距離をリアルタイム表示
            const maxSpeedKmh = 190;
            const bestAngle = 27.5;
            const angle = (this as any).flyBallAngle || 0;
            const angleDiff = Math.abs(angle - bestAngle);
            const timingResult = (this as any).flyBallTimingResult;

            // タイミング係数
            let timingMultiplier = 1.0;
            if (timingResult === TimingResult.BEST) {
              timingMultiplier = 1.0;
            } else if (timingResult === TimingResult.EARLY || timingResult === TimingResult.LATE) {
              timingMultiplier = 0.85;
            } else if (timingResult === TimingResult.VERY_EARLY || timingResult === TimingResult.VERY_LATE) {
              timingMultiplier = 0.7;
            }

            // 角度係数
            let angleMultiplier = 1.0;
            if (angleDiff <= 2.5) {
              angleMultiplier = 1.0;
            } else if (angleDiff <= 5.0) {
              angleMultiplier = 0.9;
            } else if (angleDiff <= 10.0) {
              angleMultiplier = 0.8;
            } else if (angleDiff <= 20.0) {
              angleMultiplier = 0.7;
            } else {
              angleMultiplier = 0.6;
            }

            // 表示速度を計算（一度計算したら保存）
            if (!(this as any).displaySpeedKmh) {
              const isBestTimingAndAngle = timingResult === TimingResult.BEST && angleDiff <= 2.5;
              if (isBestTimingAndAngle && Math.random() < 0.3) {
                (this as any).displaySpeedKmh = maxSpeedKmh;
              } else {
                (this as any).displaySpeedKmh = maxSpeedKmh * timingMultiplier * angleMultiplier;
              }
            }

            // 表示倍率を使用して表示用距離を計算
            const displayMultiplier = (this as any).displayMultiplier || 6.5; // デフォルト6.5倍
            const displayDistanceMx = displayDistanceM * displayMultiplier;

            // デバッグログ（リアルタイム表示の計算値）
            // リアルタイムで表示している値をそのままログに出力
            console.log('リアルタイム表示:', {
              displayDistanceMx, // 画面上に表示されている値
              displayDistanceM,
              displayMultiplier,
              currentDistancePx,
              hrDistanceM,
              actualRangePx,
              pixelsPerMeterForActual,
              finalDisplayDistanceMx: (this as any).finalDisplayDistanceMx, // ボールが止まった時に保存した値
            });

            // 最終的な距離を表示（ボールが止まった場合はfinalDisplayDistanceMxを使用）
            const finalDistance = this.ballStopped && (this as any).finalDisplayDistanceMx !== undefined
              ? (this as any).finalDisplayDistanceMx
              : displayDistanceMx;
            // Angleの値を取得（既に定義されているangle変数を使用）
            const displayAngle = angle; // 実際の角度（0度=水平、90度=直角）
            this.debugText.setText(
              `打球速度: ${(this as any).displaySpeedKmh?.toFixed(1) || '0.0'} km/h\n飛距離: ${finalDistance.toFixed(1)} m\n角度: ${Math.floor(displayAngle)}°`
            );
            this.debugText.setVisible(true);
          } else {
            // データが揃っていない場合は速度だけ表示
            const displayAngle = (this as any).flyBallAngle || 0;
            this.debugText.setText(
              `打球速度: ${(this as any).displaySpeedKmh?.toFixed(1) || '0.0'} km/h\n角度: ${Math.floor(displayAngle)}°`
            );
            this.debugText.setVisible(true);
          }
        } else {
          // 開始位置が設定されていない場合は速度だけ表示
          const displayAngle = (this as any).flyBallAngle || 0;
          this.debugText.setText(
            `打球速度: ${(this as any).displaySpeedKmh?.toFixed(1) || '0.0'} km/h\n角度: ${Math.floor(displayAngle)}°`
          );
          this.debugText.setVisible(true);
        }
      }

      // フェンスの見た目も物理ボディと同期
      if (this.fenceBody && this.fenceVisual) {
        this.fenceVisual.x = this.fenceBody.position.x;
        this.fenceVisual.y = this.fenceBody.position.y;
      }

      // 中央をまたいだ瞬間だけ追従開始（縦固定・横のみ）
      if (this.hitBallBody && !this.ballStopped) {
        const cam = this.cameras.main;

        // 縦は常にゲーム開始時で固定
        cam.scrollY = this.gameStartCamY;

        const centerX = cam.width * 0.5;
        const currScreenX = this.hitBallBody.x - cam.scrollX;

        // 1) 中央に来るまで追従しない（カメラscrollXは触らない）
        if (this.waitFollowUntilCentered) {
          const prev = this.prevBallScreenX;

          // 中央を「またいだ」判定（高速でも必ず検出できる）
          const crossed =
            prev !== null &&
            (prev - centerX) * (currScreenX - centerX) <= 0;

          if (crossed) {
            // 追従開始（ここから横だけ追う）
            this.waitFollowUntilCentered = false;
            this.followXOnly = true;

            // 追従の立ち上げを滑らかに（開始時のカクつき防止）
            this.followStrength = 0;
            this.tweens.add({
              targets: this,
              followStrength: 1,
              duration: 250,
              ease: 'Sine.easeOut',
            });
          }

          // 次フレーム用
          this.prevBallScreenX = currScreenX;
          return; // 待機中はここで終わり（scrollXを絶対動かさない）
        }

        // 2) 追従ONになったら、横だけ中央へ滑らかに寄せる
        if (this.followXOnly) {
          const desiredScrollX = this.hitBallBody.x - centerX;
          const lerp = this.followLerpMax * this.followStrength;
          cam.scrollX += (desiredScrollX - cam.scrollX) * lerp;

          // VERY_EARLYの時は、ボールが左方向に飛ぶので、カメラも左方向に追従する必要がある
          // より積極的に追従するため、追加の補正を加える
          const flyBallTimingResult = (this as any).flyBallTimingResult;
          if (flyBallTimingResult === TimingResult.VERY_EARLY) {
            // VERY_EARLYの時は、カメラが確実に追従するように確認
            // ボールが画面外に出ないように、より積極的に追従
            const distance = Math.abs(desiredScrollX - cam.scrollX);
            if (distance > 50) {
              // ボールが画面から大きく外れている場合は、より強く追従
              cam.scrollX += (desiredScrollX - cam.scrollX) * 0.2;
            }
          }
        }
      }

      // --- 衝突が取れない時の保険（ここから） ---
      const groundTopY = (this as any).flyBallGroundTopY as number | undefined;
      const flyStart = (this as any).flyBallStartTime as number | undefined;

      if (groundTopY !== undefined && this.hitBallBody.body) {
        const vy = this.hitBallBody.body.velocity.y;

        // 下向きに落ちてきて、地面高さに到達したら「着地した扱い」にする
        // （衝突イベントが来なくても ballLanded を立てる）
        if (!this.ballLanded && vy > 0 && this.hitBallBody.y >= groundTopY - 12) {
          this.ballLanded = true;
        }

        // それでも反発も減速もせずに下へ落ち続けるケースがあるので、
        // 地面より十分下に行ったら強制終了（=結果表示へ進める）
        if (this.ballLanded && !this.ballStopped && this.hitBallBody.y > groundTopY + 800) {
          // ボールが止まった時点の実際の移動距離を保存
          const startX = (this as any).flyBallStartX;
          const hrDistanceM = (this as any).hrDistanceM;
          const actualRangePx = (this as any).actualRangePx;
          if (startX !== undefined && hrDistanceM !== undefined && actualRangePx !== undefined) {
            const currentDistancePx = Math.abs(ballWorldX - startX);
            const pixelsPerMeterForActual = actualRangePx / hrDistanceM;
            const displayDistanceM = currentDistancePx / pixelsPerMeterForActual;

            // 表示倍率を使用して表示用距離を計算
            const displayMultiplier = (this as any).displayMultiplier || 6.5; // デフォルト6.5倍
            const displayDistanceMx = displayDistanceM * displayMultiplier;

            // ボールが止まった時点の実際の移動距離（表示倍率適用後）を保存
            (this as any).finalDisplayDistanceMx = displayDistanceMx;

            // HR判定を実際の移動距離（表示倍率適用後）で行う
            const finalResult = displayDistanceMx >= this.config.hrDistanceThreshold ? HitResult.HR : HitResult.MISS;
            (this as any).flyBallResult = finalResult;
            (this as any).flyBallDistance = displayDistanceMx;

            console.log('ボール停止（強制終了）: 実際の移動距離で判定', {
              displayDistanceMx,
              displayMultiplier,
              hrDistanceThreshold: this.config.hrDistanceThreshold,
              finalResult,
            });
          }
          this.ballStopped = true;
          this.waitFollowUntilCentered = false;
          this.followXOnly = false;
          this.prevBallScreenX = null;
          this.followStrength = 0;

          if (this.hitCutTimer) {
            this.hitCutTimer.remove(false);
            this.hitCutTimer = null;
          }

          // 結果を表示（ボールが停止している画面で表示）
          const result = (this as any).flyBallResult;
          const distance = (this as any).flyBallDistance;
          const timingResult = (this as any).flyBallTimingResult;
          const angle = (this as any).flyBallAngle;

          // スコア計算（distanceは既にdisplayDistanceM7_5xなので、そのまま使用）
          const score = result === HitResult.HR ? Math.floor(distance * this.config.scorePerMeter) : 0;

          const pitchResult: PitchResult = {
            result: result,
            distance: distance,
            score: score,
            timingResult: timingResult,
            angle: angle,
          };
          this.pitchResults.push(pitchResult);

          // ボールが停止している画面で結果を表示（画面切り替え前）
          this.showResultOnBallStoppedScreen(pitchResult);
        }
      }

      // 最終保険：一定時間たっても決着しないなら強制終了（お好みで）
      if (flyStart !== undefined && !this.ballStopped && this.time.now - flyStart > 8000) {
        this.ballLanded = true; // 以降の処理で終わらせる
      }
      // --- 衝突が取れない時の保険（ここまで） ---

      // 地面はsetScrollFactor(0,0)で固定されているので、tilePositionXを更新しない
      // これにより、地面は画面に固定され、ボールと一緒に動かない

      // ボールが止まったかチェック（着地後、速度がほぼ0になったら）
      if (this.ballLanded && !this.ballStopped && this.hitBallBody.body) {
        const body = this.hitBallBody.body as MatterJS.BodyType;
        const velocity = this.hitBallBody.body.velocity;
        const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);

        // 転がりを強力に抑えるため、角速度（回転速度）を積極的に減衰させる
        if ((body as any).angularVelocity) {
          const currentAngularVelocity = (body as any).angularVelocity;
          // 角速度を0.7倍に減衰（毎フレーム30%減衰、より積極的に）
          (body as any).angularVelocity = currentAngularVelocity * 0.7;
          // 角速度が非常に小さい場合は0に設定
          if (Math.abs(currentAngularVelocity) < 0.05) {
            (body as any).angularVelocity = 0;
          }
        }

        // 速度が低い時は角速度と速度の両方を強制的に減らす
        if (speed < 15) {
          // 角速度をさらに減らす
          if ((body as any).angularVelocity) {
            (body as any).angularVelocity = (body as any).angularVelocity * 0.5;
          }
          // 速度も減衰させる（転がりを抑える）
          if (speed > 0.5) {
            const velocityX = velocity.x * 0.9; // 10%減衰
            const velocityY = velocity.y * 0.9; // 10%減衰
            this.hitBallBody.setVelocity(velocityX, velocityY);
          }
        }

        // 速度が1以下で、かつ数フレーム連続でその状態が続いたら完全に停止と判定
        if (speed < 1) {
          this.ballStoppedFrameCount++;
          // 10フレーム連続で速度が1以下なら完全に停止と判定
          if (this.ballStoppedFrameCount >= 10) {
            // ボールが止まった時点の実際の移動距離を保存
            const startX = (this as any).flyBallStartX;
            const hrDistanceM = (this as any).hrDistanceM;
            const actualRangePx = (this as any).actualRangePx;
            if (startX !== undefined && hrDistanceM !== undefined && actualRangePx !== undefined) {
              const currentDistancePx = Math.abs(ballWorldX - startX);
              const pixelsPerMeterForActual = actualRangePx / hrDistanceM;
              const displayDistanceM = currentDistancePx / pixelsPerMeterForActual;

              // 表示倍率を使用して表示用距離を計算
              const displayMultiplier = (this as any).displayMultiplier || 6.5; // デフォルト6.5倍
              const displayDistanceMx = displayDistanceM * displayMultiplier;

              // ボールが止まった時点の実際の移動距離（表示倍率適用後）を保存
              (this as any).finalDisplayDistanceMx = displayDistanceMx;

              // HR判定を実際の移動距離（表示倍率適用後）で行う
              const finalResult = displayDistanceMx >= this.config.hrDistanceThreshold ? HitResult.HR : HitResult.MISS;
              (this as any).flyBallResult = finalResult;
              (this as any).flyBallDistance = displayDistanceMx;

              console.log('ボール停止: 実際の移動距離で判定', {
                displayDistanceMx,
                displayMultiplier,
                hrDistanceThreshold: this.config.hrDistanceThreshold,
                finalResult,
              });
            }
            this.ballStopped = true;
            this.ballStoppedFrameCount = 0; // リセット
            this.waitFollowUntilCentered = false;
            this.followXOnly = false;
            this.prevBallScreenX = null;
            this.followStrength = 0;

            // 未発火の切り替えタイマーがあれば止める
            if (this.hitCutTimer) {
              this.hitCutTimer.remove(false);
              this.hitCutTimer = null;
            }

            // 結果を表示（ボールが停止している画面で表示）
            const result = (this as any).flyBallResult;
            const distance = (this as any).flyBallDistance;
            const timingResult = (this as any).flyBallTimingResult;
            const angle = (this as any).flyBallAngle;

            // ボールを打ったがHRにならなかった場合はストライクにしない
            // （見逃しMISSと空振りMISSのみがストライクになる）

            // スコア計算（distanceは既にdisplayDistanceM7_5xなので、そのまま使用）
            const score = result === HitResult.HR ? Math.floor(distance * this.config.scorePerMeter) : 0;

            const pitchResult: PitchResult = {
              result: result,
              distance: distance,
              score: score,
              timingResult: timingResult,
              angle: angle,
            };
            this.pitchResults.push(pitchResult);

            // ボールが停止している画面で結果を表示（画面切り替え前）
            this.showResultOnBallStoppedScreen(pitchResult);
          }
        } else {
          // 速度が1以上なら、停止フレームカウントをリセット
          this.ballStoppedFrameCount = 0;
        }
      }
    } else {
      // 打球が存在しないときは前回位置をリセット
      this.prevHitBallWorldX = null;
      this.prevHitBallWorldY = null;
    }

    // 角度メーターのポインターを動かす（動いている場合のみ）
    if (this.angleMeterPointer && this.angleBarBg) {
      // ポインターが動いている場合のみ更新
      if (this.isCursorMoving) {
        // 時間を更新（Phaser 3の正しい方法）
        let deltaTime = this.game.loop.delta;
        if (isNaN(deltaTime) || deltaTime === undefined || deltaTime <= 0) {
          deltaTime = 16; // フォールバック（60fps想定）
        }

        // angleBarTimeを初期化（NaNチェック）
        if (isNaN(this.angleBarTime)) {
          this.angleBarTime = 0;
        }
        this.angleBarTime += deltaTime;

        // 速度を設定（一定速度で動かす）- 遅くする
        const speed = 0.05; // 以前の半分の速度
        this.angleBarSpeed = speed;

        // サイン波で半円上を往復（0から1の範囲）
        // angleBarTime=0の時、normalizedPos=0になるように位相を調整（-π/2シフト）
        // これにより、角度メーターは0度（下=実際の0度=水平）から90度（上=実際の90度=垂直）へ動く
        const normalizedPos = (Math.sin((this.angleBarTime * speed) / 10 - Math.PI / 2) + 1) / 2;

        // 角度を更新（角度メーター上：0度=下（水平）、90度=上（直角））
        const angleRange = this.config.barMaxAngle - this.config.barMinAngle;
        // 角度メーター上の角度：0度=下、90度=上
        // normalizedPos=0の時、currentAngle=0度（下=実際の0度=水平）
        // normalizedPos=1の時、currentAngle=90度（上=実際の90度=垂直）
        this.currentAngle = this.config.barMinAngle + normalizedPos * angleRange;

        // 角度を1/4円の範囲にマッピング
        const quarterAngle = this.mapAngleToQuarterCircle(this.currentAngle);
        const rad = Phaser.Math.DegToRad(quarterAngle);

        // ポインターの位置を更新（右下の角から伸びる）
        if (this.angleMeterPointer) {
          const pointerLength = this.angleMeterRadius * 0.7; // ポインターの長さ（半径の70%）
          const endX = this.angleMeterOriginX + Math.cos(rad) * pointerLength;
          const endY = this.angleMeterOriginY - Math.sin(rad) * pointerLength; // sinの符号を反転（270度=下方向にするため）
          this.angleMeterPointer.setTo(
            this.angleMeterOriginX, this.angleMeterOriginY, // 原点
            endX, endY // 終点
          );
          this.angleMeterPointer.setVisible(true);
          this.angleMeterPointer.setAlpha(1.0);
        }
      }
    }

    // デバッグ情報の更新とタイミングバーのポインター更新
    // 空振り判定後は更新しない（isSwingMissedがtrueの場合はスキップ）
    if ((this.gameState === 'pitching' || this.gameState === 'timing') && !this.isSwingMissed) {
      // タイミングバーのポインターを更新（投球開始と同時に移動開始）
      if (this.timingBarPointer && this.timingBarBg && this.timingStartTime > 0 && this.pitchDuration > 0) {
        let percentage: number;
        const barHalfWidth = (this.timingBarBg.displayWidth || this.timingBarBg.width) / 2;
        const barMinX = this.timingBarBg.x - barHalfWidth;
        const barMaxX = this.timingBarBg.x + barHalfWidth;

        // 常にボールの真下にカーソルを合わせる（ボールが表示中の場合）
        if (this.ball && this.ball.visible) {
          const ballX = this.hasSwingBallPos ? this.swingBallScreenX : this.ball.x;
          const catcherCenterX = this.catcherScreen ? this.catcherScreen.x : this.ballTargetX;
          const startX = this.ballStartX;
          const totalDist = catcherCenterX - startX;
          const ratio = totalDist !== 0 ? (ballX - startX) / totalDist : 0;
          const clampedRatio = Phaser.Math.Clamp(ratio, 0, 1);
          const pointerX = this.timingBarStartX + (clampedRatio * this.timingBarWidth);
          this.timingBarPointer.setX(pointerX);

          percentage = clampedRatio * 100;
        } else {
          // ボールが非表示の場合は時間ベースで更新
          const currentTime = this.time.now - this.timingStartTime;
          percentage = (currentTime / this.pitchDuration) * 100;
          const normalizedPercentage = Phaser.Math.Clamp(percentage / 100, 0, 1);
          const pointerX = barMinX + (normalizedPercentage * (barMaxX - barMinX));
          this.timingBarPointer.setX(pointerX);
        }

        // ポインターの色を現在のタイミングに応じて変更
        // タイミングバー画像の色に対応
        // 空振り判定（0-70%または93-100%）- 赤ゾーン
        if (percentage < 70 || percentage >= 93) {
          // 空振り範囲（赤色）
          this.timingBarPointer.setFillStyle(0xff0000); // 赤（空振り）
        } else if (percentage >= 81 && percentage < 85) {
          // ベストタイミング（81-85%）- 緑ゾーン
          this.timingBarPointer.setFillStyle(0x00ff00); // 緑
        } else if (percentage >= 76 && percentage < 81) {
          // 早い（76-81%）- 左黄色ゾーン
          this.timingBarPointer.setFillStyle(0xffff00); // 黄
        } else if (percentage >= 70 && percentage < 76) {
          // とても早い（70-76%）- 左オレンジゾーン
          this.timingBarPointer.setFillStyle(0xff8800); // オレンジ
        } else if (percentage >= 85 && percentage < 89) {
          // 遅い（85-89%）- 右黄色ゾーン
          this.timingBarPointer.setFillStyle(0xffff00); // 黄
        } else if (percentage >= 89 && percentage < 93) {
          // とても遅い（89-93%）- 右オレンジゾーン
          this.timingBarPointer.setFillStyle(0xff8800); // オレンジ
        } else {
          // 念のため、範囲外の場合は空振りとして扱う
          this.timingBarPointer.setFillStyle(0xff0000); // 赤
        }
      }

      // timingやpitching状態では、update内では上書きしない
      // 実際の値は打球が飛び始めた時に表示される
    } else if (this.gameState === 'result' && this.finalTimingPointerX !== null) {
      // 結果表示中も、タイミングバーのポインターを最終位置に維持
      if (this.timingBarPointer) {
        this.timingBarPointer.setX(this.finalTimingPointerX);
      }
    } else if (this.gameState === 'angleBar') {
      // 角度決定中は0を表示（確定するまで）
      this.debugText.setText(
        `打球速度: 0 km/h\n飛距離: 0 m\n角度: 0°`
      );
    }
  }

  private determineHit(): void {
    if (this.gameState !== 'angleBar') {
      return; // 角度バー操作中でない場合は無視
    }

    // バッターのスイングアニメーションを4-5フレームから再生（角度確定時）
    if (this.batter && this.anims.exists('batter_swing')) {
      console.log('[Batter Animation] 角度確定 - 4-5フレームから再生開始');

      // 既存のイベントリスナーをクリア（重複防止）
      this.batter.off('animationupdate');
      this.batter.off('animationcomplete');

      // アニメーションを停止
      if (this.batter.anims.isPlaying) {
        this.batter.anims.stop();
      }

      // フレーム4-5から再生するアニメーションを作成
      const frames = this.anims.generateFrameNumbers('batter_ss', { start: 4, end: 5 });
      if (!this.anims.exists('batter_swing_part2')) {
        this.anims.create({
          key: 'batter_swing_part2',
          frames: frames, // 4-5フレーム
          frameRate: 15,
          repeat: 0,
        });
      }

      // アニメーションを再生（4-5フレーム）
      this.batter.play('batter_swing_part2');
    }

    // カーソルを停止
    this.isCursorMoving = false;

    this.gameState = 'result';
    this.tapCount = 0;

    // タイミング結果を取得
    const timingResult = (this as any).lastTimingResult || TimingResult.BEST;

    // 角度メーター上の角度を実際の打球角度に変換
    // 角度メーター：0度=左（水平）、90度=上（垂直）
    // 実際の角度：0度=水平、90度=垂直
    // 角度メーター上の角度がそのまま実際の角度になる
    const actualAngle = this.currentAngle;

    // 現在のカーソル位置が赤い部分（Best帯）内かどうかを再計算
    // ベスト角度は30度（25度から35度がベスト帯、バレルゾーン）
    const bestBandCenter = 30; // 実際のベスト角度（度）：0度=水平、90度=直角
    const bestBandHalf = this.config.bestBandWidth / 2;
    const angleDiff = Math.abs(actualAngle - bestBandCenter);
    const isInBest = angleDiff <= bestBandHalf;

    // タイミングと角度から距離を計算（参考用、実際の飛距離は後で計算）
    // calculateDistanceは使わない（実際の物理的な飛距離を採用する）

    // HR判定は後で実際の物理的な飛距離で行う（一旦MISSとして設定）
    const result = HitResult.MISS;

    // 角度の結果はdebugTextに統合されているため、angleResultTextは非表示にする
    if (this.angleResultText) {
      this.angleResultText.setVisible(false);
    }

    // スコア計算は後で実際の物理的な飛距離から行う（一旦0）
    // const score = 0; // 後で実際の飛距離から計算
    // this.totalScore += score;

    // 打球を飛ばす
    // スイングした場合はすべて打球を飛ばす（すべてのタイミング結果で打球を飛ばす）
    // 空振りは発生しない（スイングしなかった場合のみ空振り）
    const isSwingMiss = false; // スイングした場合はすべて打球を飛ばす

    if (isSwingMiss) {
      // 空振り（とても早い/とても遅い）の場合は飛ばない
      // ボールをキャッチャーと重なる位置まで移動させて非表示にする
      if (this.ball && this.catcherScreen) {
        const catcherX = this.catcherScreen.x; // キャッチャーと重なる位置
        const catcherY = this.catcherScreen.y - 40; // キャッチャーの手の位置

        // ボールのdepthをキャッチャーより下にする（キャッチャーの後ろに隠れるように）
        if (this.catcher) {
          const catcherDepth = this.catcher.depth;
          this.ball.setDepth(catcherDepth - 1); // キャッチャーより1つ下
        }

        // ボールをキャッチャーと重なる位置まで移動させるアニメーション
        this.tweens.add({
          targets: this.ball,
          x: catcherX,
          y: catcherY,
          duration: 300, // 300msで移動
          ease: 'Power2',
          onComplete: () => {
            // アニメーション完了後、ボールを非表示にする（ミットに入ったように見せる）
            if (this.ball) {
              this.ball.setVisible(false);
            }

            // 結果を表示
            const pitchResult: PitchResult = {
              result: HitResult.MISS,
              distance: 0,
              score: 0,
              timingResult: timingResult,
              angle: actualAngle, // 実際の角度を保存
            };
            this.pitchResults.push(pitchResult);
            this.showResult(pitchResult);
          },
        });
      } else {
        // ボールやキャッチャーが存在しない場合は即座に結果を表示
        const pitchResult: PitchResult = {
          result: HitResult.MISS,
          distance: 0,
          score: 0,
          timingResult: timingResult,
          angle: actualAngle,
        };
        this.pitchResults.push(pitchResult);
        this.showResult(pitchResult);
      }
    } else {
      // 打球を飛ばす（HRでもMISSでも飛ぶ）
      // 実際の角度（0度=水平、90度=直角）を渡す
      // 距離は後で実際の物理的な飛距離から計算する（一旦0を渡す）
      this.flyBall(0, actualAngle, timingResult, result);
    }
  }

  private flyBall(distance: number, angle: number, timingResult: TimingResult, result: HitResult): void {
    // 地面を明示的に表示状態にする（打った瞬間に消えないように）
    if (this.ground) {
      this.ground.setVisible(true);
      this.ground.setAlpha(1.0);
    }
    
    // 球をバッターが打った音を再生
    this.sound.play('ball_hit', { volume: 0.5 });
    // バットにボールが当たり飛んでいった時の歓声を再生
    this.sound.play('cheers', { volume: 0.5 });
    // フェンス衝突音フラグをリセット
    this.fenceHitSoundPlayed = false;

    // タイミング結果を最初に保存（カメラ追従の設定で使用するため）
    (this as any).flyBallTimingResult = timingResult;
    (this as any).flyBallAngle = angle;
    // 距離の補間用の前回値をリセット
    (this as any).previousDisplayDistanceM = undefined;

    // 打球を作成（"スイング開始時点で止めた投球ボール位置"から出す）
    const { width, height } = this.scale;
    // "スイング開始時点で止めた投球ボール位置"を優先、なければバット位置
    const hitScreenX = this.hasSwingBallPos ? this.swingBallScreenX : (this.bat ? (this.bat.x - 10) : (this.ballTargetX - 30));
    const hitScreenY = this.hasSwingBallPos ? this.swingBallScreenY : (this.bat ? (this.bat.y - 70) : (this.ballTargetY - 50));

    const hitWorld = this.cameras.main.getWorldPoint(hitScreenX, hitScreenY);
    const startX = hitWorld.x;
    const startY = hitWorld.y;

    // カメラの現在のスクロール位置を取得（地面ボディの配置に必要）
    const cameraX = this.cameras.main.scrollX;
    const cameraY = this.cameras.main.scrollY;

    // カメラの初期位置を保存（戻し処理用）
    (this as any).flyBallInitialCameraX = cameraX;
    (this as any).flyBallInitialCameraY = cameraY;
    // 打球開始位置を保存（リアルタイム飛距離計算用）
    (this as any).flyBallStartX = startX;
    (this as any).flyBallStartY = startY;
    // 表示速度をリセット（次の打球用）
    (this as any).displaySpeedKmh = null;
    // pixelsPerMeterを先に定義（後でactualRangePxをメートルに変換する際に使用）
    const pixelsPerMeter = (width / 10) * 5; // 見た目だけ：1mあたりピクセル数を5倍

    const groundHeight = height * 0.3;
    const groundYPos = height - groundHeight;

    // 角度から飛ぶ方向を計算（度からラジアンに変換）
    // 角度は上向きが正（0度が水平、正の値が上向き）
    // バッターが打ったボールは左方向（ピッチャー側）に飛ぶ
    const angleRad = Phaser.Math.DegToRad(angle);

    // タイミングと角度から目標飛距離を計算（初速度計算に使用）
    // ベスト角度は30度（0度=水平、90度=直角、バレルゾーン：25度〜35度）
    const bestBandCenter = 30;
    const bestBandHalf = this.config.bestBandWidth / 2;
    const angleDiff = Math.abs(angle - bestBandCenter);
    const isInBest = angleDiff <= bestBandHalf;

    // 表示倍率を決定
    let displayMultiplier = 6.0; // デフォルトは6.0倍（6.5から6.0に下げる）

    if (isInBest) {
      // バレルゾーン（25度〜35度）内の場合
      // 30度の時は7.5倍、30度以外はさらに和らげる
      if (angleDiff === 0) {
        // 30度の時：7.5倍
        displayMultiplier = 7.5;
        // タイミングベスト＋角度30度の場合、10％の確率で8倍になる
        if (timingResult === TimingResult.BEST) {
          if (Math.random() < 0.1) {
            displayMultiplier = 8.0;
          }
        }
      } else {
        // 30度以外（25度〜35度、30度以外）：5.8倍から7.0倍まで線形補間（6.3倍から5.8倍に下げる）
        displayMultiplier = 7.0 - angleDiff * 0.24; // 5.8倍から7.0倍まで
      }
    }

    (this as any).displayMultiplier = displayMultiplier; // 他の箇所で使用するために保存

    // タイミングと角度から目標飛距離を計算（メートル）
    const targetDistanceM = this.calculateDistance(timingResult, angle, isInBest);

    // 目標飛距離をピクセルに変換（初速度計算用）
    const targetRangePx = targetDistanceM * pixelsPerMeter;

    // 飛距離は後で実際の物理的な飛距離から計算する（一旦仮の値を使用）
    // 地面ボディの配置には、後で計算されるactualRangePxを使う
    const rangePx = targetRangePx; // タイミングと角度から計算した目標飛距離を使用

    // 既存の物理ボディを削除して再作成
    if (this.groundBody) {
      this.matter.world.remove(this.groundBody);
      this.groundBody = null;
    }
    if (this.fenceBody) {
      this.matter.world.remove(this.fenceBody);
      this.fenceBody = null;
    }
    if (this.fenceVisual) {
      this.fenceVisual.destroy();
      this.fenceVisual = null;
    }
    // 距離メモリを削除
    if (this.distanceMarkers) {
      this.distanceMarkers.clear(true, true);
      this.distanceMarkers = null;
    }
    if (this.hitBall) {
      this.hitBall.destroy();
      this.hitBall = null;
    }
    if (this.hitBallBody) {
      this.hitBallBody.destroy();
      this.hitBallBody = null;
    }

    // ピッチャーが投げたボールを削除（バッターが打ったので）
    if (this.ball) {
      this.ball.destroy();
      this.ball = null;
    }
    
    // 地面を明示的に表示状態にする（ball.destroy()の後に消えないように）
    if (this.ground) {
      this.ground.setVisible(true);
      this.ground.setAlpha(1.0);
      this.ground.setDepth(0);
    }

    // 地面の物理ボディを再作成（飛ぶ範囲に合わせて配置）
    // 表示用距離が最大250mになる可能性を考慮して、それに対応する実際の物理的な距離を計算
    const maxDisplayDistanceM = 250; // 最大の表示用距離（メートル）
    const minDisplayMultiplier = 6.0; // 最小の表示倍率（バレルゾーン外）
    const maxActualDistanceM = maxDisplayDistanceM / minDisplayMultiplier; // 最大の実際の物理的な距離
    const maxActualRangePx = maxActualDistanceM * pixelsPerMeter; // 最大の実際の物理的な距離（ピクセル）
    
    const buffer = width * 2; // 余白
    // 目標飛距離と最大実際距離の大きい方を使用して地面の幅を決定
    const maxRange = Math.max(rangePx, maxActualRangePx);
    // 転がり・跳ね返りを考慮して左方向に追加の余白を確保
    const extraRollPx = width * 4;
    // 地面の幅：ボールが飛ぶ範囲 + バッター後方の余白 + 左方向の追加余白
    const groundWidth = maxRange + buffer * 2 + extraRollPx;
    // 地面の中心位置：右端を startX + buffer に固定して左方向に余白を追加
    // 右端: startX + buffer, 左端: startX - maxRange - buffer - extraRollPx
    const groundCenterX = startX - (maxRange + extraRollPx) / 2;
    const groundCenterY = cameraY + groundYPos + groundHeight / 2;
    
    // executeHitCut関数で使用する変数を保存（クロージャで参照できるように）
    // これらの変数はexecuteHitCutが定義される前に定義されている必要がある

    console.log('地面の物理ボディ再作成:', {
      rangePx,
      groundYPos,
      groundHeight,
      groundCenterX,
      groundCenterY,
      groundWidth,
      cameraX,
      cameraY,
      ballStartX: startX,
      ballStartY: startY,
    });

    this.groundBody = this.matter.add.rectangle(
      groundCenterX,
      groundCenterY,
      groundWidth,
      groundHeight,
      {
        isStatic: true,
        friction: 8.0, // 摩擦係数（大きいほど転がりにくい、転がりを抑えるにはこちらを調整）
        frictionStatic: 2.0, // 静止摩擦係数（静止状態から動き始める際の摩擦、転がりには影響しない）
        label: 'ground',
      }
    );

    if (this.groundBody) {
      this.matter.body.setStatic(this.groundBody, true);
      console.log('地面の物理ボディ位置確認:', {
        x: this.groundBody.position.x,
        y: this.groundBody.position.y,
        width: groundWidth,
        height: groundHeight,
        ballX: startX,
        ballY: startY,
        distanceY: Math.abs(this.groundBody.position.y - startY),
        groundRange: `${this.groundBody.position.x - groundWidth / 2} ~ ${this.groundBody.position.x + groundWidth / 2}`,
      });
    }

    // 距離メモリは後で作成（actualRangePxを計算してから）
    // フェンスを作成（displayDistanceMxの95mの場所）
    // 表示倍率に応じて実際の距離を計算
    const hrDistanceM = 95; // HR判定距離（メートル、表示倍率適用後）
    // 画像のサイズを取得（リサイズせず使用）
    const fenceTexture = this.textures.get('fence');
    const fenceImage = fenceTexture.getSourceImage() as HTMLImageElement;
    const fenceWidth = fenceImage.width; // 画像の幅
    const fenceHeightPx = fenceImage.height; // 画像の高さ
    // 表示倍率を使用して実際の距離を計算（既に計算済みのdisplayMultiplierを使用）
    const actualHrDistanceM = hrDistanceM / ((this as any).displayMultiplier || 6.5); // 実際の距離（表示倍率適用後の95mを実際の距離に変換）
    const fenceX = startX - actualHrDistanceM * pixelsPerMeter; // バッターから左方向に実際の距離（表示倍率適用後で95m）
    const groundTopY = groundCenterY - groundHeight / 2; // 地面の上端Y座標
    const fenceY = groundTopY - fenceHeightPx / 2; // フェンスの下端を地面の上端に合わせる（画像の中心位置、Y座標は上向きが小さい）

    console.log('フェンス作成:', {
      startX,
      hrDistanceM,
      pixelsPerMeter,
      fenceX,
      fenceY,
      fenceWidth,
      fenceHeightPx,
      groundCenterY,
      groundHeight,
    });

    // フェンスの物理ボディ（静的）
    this.fenceBody = this.matter.add.rectangle(
      fenceX,
      fenceY,
      fenceWidth,
      fenceHeightPx,
      {
        isStatic: true,
        label: 'fence',
      }
    );

    if (this.fenceBody) {
      this.matter.body.setStatic(this.fenceBody, true);
    }

    // フェンスの見た目（画像を使用、リサイズせず）
    this.fenceVisual = this.add.image(
      fenceX,
      fenceY,
      'fence'
    );
    this.fenceVisual.setDepth(10000); // より前面に表示（非常に高い深度）
    this.fenceVisual.setScrollFactor(1, 1); // カメラに追従
    this.fenceVisual.setVisible(true); // 明示的に表示

    // 地面の上端Y（ワールド座標）を保存
    (this as any).flyBallGroundTopY = groundCenterY - groundHeight / 2;

    // 打球開始時刻を保存
    (this as any).flyBallStartTime = this.time.now;

    // デバッグカウントもリセット（次の打席でログが出るように）
    (this as any).ballDebugCount = 0;

    // 打った直後は打席を見せる（250ms後に打球フェーズへ切り替え）
    // まずは打席の見た目を維持（地面もキャラも画面固定のまま）
    // ※ ground/pitcher/batter/bat は create() で setScrollFactor(0,0) 済みなので何もしなくてOK

    // 物理ボディとしてボールを作成（ball画像を使用）
    this.hitBallBody = this.matter.add.sprite(startX, startY, 'ball', undefined, {
      shape: {
        type: 'circle',
        radius: 12,
      },
      frictionAir: 0.003, // 空気抵抗（小さいほど滞空時間が長い）
      friction: 8.0, // 摩擦係数（大きいほど転がりにくい、転がりを抑えるにはこちらを調整）
      restitution: 0.4, // 反発係数（バウンス、少し跳ねる）
      density: 0.003, // 密度（上げると慣性が大きくなり、転がりにくくなる）
      ignoreGravity: false, // 重力を有効にする（明示的に）
      isStatic: false, // 動的ボディ（明示的に設定）
      label: 'ball', // デバッグ用ラベル
    });
    this.hitBallBody.setDepth(200); // バッター（depth 1）より上に表示（確実に前面に）

    // 物理ボディが動的であることを確認
    if (this.hitBallBody.body) {
      const body = this.hitBallBody.body as MatterJS.BodyType;
      this.matter.body.setStatic(body, false);
      console.log('ボールの物理ボディ作成:', {
        x: this.hitBallBody.x,
        y: this.hitBallBody.y,
        isStatic: (body as any).isStatic,
        ignoreGravity: (body as any).ignoreGravity,
      });
    }
    // 打球の物理ボディはワールド座標で配置されているので、カメラのスクロールに追従させる
    this.hitBallBody.setScrollFactor(1, 1);

    // 見た目は物理ボディと同じ画像を使用（物理ボディと同期、ワールド座標で配置）
    this.hitBall = this.add.image(startX, startY, 'ball');
    this.hitBall.setDepth(200); // バッター（depth 1）より上に表示（確実に前面に）
    // 打球の見た目もワールド座標で配置されているので、カメラのスクロールに追従させる
    this.hitBall.setScrollFactor(1, 1);
    // 画像サイズはリサイズせずそのまま使用（30x30ピクセル）

    // 元のボールは非表示にしない（位置を変えずに表示し続ける）
    // this.ball.setVisible(false); // コメントアウト：ボールの位置を変えずに表示

    // 初速度を計算（飛距離と角度から）
    // Matter.jsの実際の重力加速度を取得（1ステップあたりの単位）
    const engine = this.matter.world.engine;
    // Matter.jsの重力は engine.world.gravity から取得
    const g = (engine as any).world?.gravity?.y || 1.5; // 1ステップあたりの重力加速度（実際の値）

    // 飛距離から初速度を逆算（物理計算）
    // rangePxはタイミングと角度から計算した目標飛距離（ピクセル）
    // これにより、タイミングがベストで角度が30度の時に最も飛ぶ（バレルゾーン：25度〜35度）
    const rangePxForVelocity = rangePx || 10000;

    // 初速度の大きさを計算（放物線運動の公式から逆算）
    // 水平距離 = v0^2 * sin(2θ) / g
    // v0 = sqrt(水平距離 * g / sin(2θ))
    const sin2 = Math.sin(2 * angleRad);
    const denom = Math.max(0.01, sin2); // ゼロ除算を防ぐ

    // v は「px/step」（1ステップあたりのピクセル数）
    // 速度を遅くする（全体を0.5倍に）ことを考慮して、目標飛距離を2倍にする
    // これにより、実際の物理的な飛距離がHR判定距離に近づく
    let v = Math.sqrt((rangePxForVelocity * 2 * g) / denom);

    // 速度を遅くする（全体を0.5倍に）
    v *= 0.5;

    // 速度を適切な範囲に制限（px/step）
    // BEST+30度で200m近く飛ぶように調整
    v = Phaser.Math.Clamp(v, 3, 60);

    // 実際の物理的な飛距離を計算（速度が減速・制限された後の実際の飛距離）
    // 実際の飛距離 = v^2 * sin(2θ) / g（ただし、vは減速・制限後の値）
    const actualRangePx = (v * v * sin2) / g;
    // 実際の物理的な飛距離を保存（リアルタイム表示用）
    (this as any).actualRangePx = actualRangePx;

    // 実際の物理的な飛距離をメートルに変換（pixelsPerMeterを使って）
    const actualDistanceM = actualRangePx / pixelsPerMeter;

    // 実際の物理的な飛距離を「飛距離」として採用
    const finalDistance = actualDistanceM;

    // 表示倍率を使用して表示用距離を計算（既に計算済みのdisplayMultiplierを使用）
    const displayDistanceMx = finalDistance * ((this as any).displayMultiplier || 6.5);

    // 実際の飛距離を保存（リアルタイム表示用）
    (this as any).hrDistanceM = finalDistance;
    (this as any).targetRangePx = actualRangePx;
    (this as any).displayDistanceMx = displayDistanceMx; // 表示用距離を保存

    // HR判定とスコア計算は、ボールが止まった時点の実際の移動距離（displayDistanceMx）で行う
    // そのため、ここでは判定しない（ボールが止まった時に判定する）

    // 初速度を設定（左方向に飛ぶ）
    const velocityX = -Math.cos(angleRad) * v; // 左方向（負の値）
    const velocityY = -Math.sin(angleRad) * v; // 上方向（負の値、PhaserはYが下向きが正）

    console.log('ボールの初速度設定:', {
      v,
      velocityX,
      velocityY,
      angle,
      angleRad,
      finalDistance,
      actualRangePx,
      g,
      sin2,
    });

    this.hitBallBody.setVelocity(velocityX, velocityY);

    // 回転（スピン）を付ける：速度に比例して回転量を変える
    const spin = Phaser.Math.Clamp(v / 30, 0, 1) * 0.25; // 0〜0.25 くらい
    this.hitBallBody.setAngularVelocity(spin * (velocityX < 0 ? -1 : 1));

    // 速度が正しく設定されているか確認
    if (this.hitBallBody.body) {
      console.log('ボールの速度確認:', {
        bodyVelocityX: this.hitBallBody.body.velocity.x,
        bodyVelocityY: this.hitBallBody.body.velocity.y,
        bodySpeed: Math.sqrt(this.hitBallBody.body.velocity.x ** 2 + this.hitBallBody.body.velocity.y ** 2),
      });
    }

    // 状態をリセット
    this.ballLanded = false;
    this.ballStopped = false;
    this.ballStoppedFrameCount = 0;

    // カメラの境界を設定（ワールドサイズを定義）
    this.cameras.main.setBounds(-10000, -10000, 20000, 20000);

    // 打球生成直後に「待機開始」（ここ重要）
    // ここでカメラは絶対動かしません
    const cam = this.cameras.main;

    // 競合要因を止める（カメラTween等）
    this.tweens.killTweensOf(cam);
    this.tweens.killTweensOf(cam.followOffset);

    // 縦固定
    cam.scrollY = this.gameStartCamY;

    // 中央待ち開始（追従はまだ開始しない）
    // VERY_EARLYの時は、ボールが左方向に飛ぶので、中央をまたぐ前に画面外に出る可能性がある
    // そのため、VERY_EARLYの時は即座に追従を開始する
    const flyBallTimingResult = (this as any).flyBallTimingResult;
    if (flyBallTimingResult === TimingResult.VERY_EARLY) {
      // VERY_EARLYの時は即座に追従を開始
      this.waitFollowUntilCentered = false;
      this.followXOnly = true;
      this.followStrength = 1; // 即座に追従
      this.prevBallScreenX = null;
    } else {
      // それ以外は中央をまたぐまで待機
      this.waitFollowUntilCentered = true;
      this.followXOnly = false;
      this.followStrength = 0;
      // 「今の画面内X」を保存（中央またぎ検出用）
      this.prevBallScreenX = this.hitBallBody ? (this.hitBallBody.x - cam.scrollX) : null;
    }

    // 打った直後の絵を見せる時間（ミリ秒）
    // VERY_EARLYの時は、ボールが左方向に飛ぶので、即座に切り替える
    // 画面外に出る前から追従したいなら 0〜80 くらいにする
    const hitCutMs = flyBallTimingResult === TimingResult.VERY_EARLY ? 0 : 50;

    // 既存タイマーが残っていたら必ず消す
    if (this.hitCutTimer) {
      this.hitCutTimer.remove(false);
      this.hitCutTimer = null;
    }

    // VERY_EARLYの時は、hitCutTimerを待たずに即座に処理を実行
    const executeHitCut = () => {
      if (this.ballStopped || !this.hitBallBody) return;

      const cam = this.cameras.main;

      // ここが超重要：この時点で「画面固定のキャラ」をワールド座標に変換する（消さない）
      if (this.pitcher) this.detachFromUIToWorld(this.pitcher);
      if (this.batter) this.detachFromUIToWorld(this.batter);
      if (this.catcher) this.detachFromUIToWorld(this.catcher);
      if (this.bat) this.detachFromUIToWorld(this.bat);

      // 地面（TileSprite）は GameObject 形が違うので個別にワールドへ移す
      // groundYPos は「画面座標」なので getWorldPoint でワールドへ
      if (!this.ground || !this.ground.active) {
        console.error('[executeHitCut] 地面が存在しないか非アクティブです。再作成します。');
        const { width, height } = this.scale;
        const groundHeight = height * 0.3;
        const groundY = height - groundHeight;
        this.ground = this.add.tileSprite(0, groundY, width, groundHeight, 'ground');
        this.ground.setOrigin(0, 0);
        this.ground.setDepth(0);
        this.ground.setTileScale(1, 1);
      }
      
      // カメラの現在位置を取得
      const currentCameraX = cam.scrollX;
      const currentCameraY = cam.scrollY;
      
      // 地面のワールド座標を計算
      const groundWorldTL = cam.getWorldPoint(0, groundYPos);
      // groundCenterXが地面の中心なので、左端は groundCenterX - groundWidth / 2
      const groundX = groundCenterX - groundWidth / 2;
      const groundY = groundWorldTL.y;
      
      console.log('[executeHitCut] 地面の位置設定（前）:', {
        currentCameraX,
        currentCameraY,
        groundCenterX,
        groundWidth,
        groundHeight,
        groundYPos,
        groundWorldTL: { x: groundWorldTL.x, y: groundWorldTL.y },
        groundX,
        groundY,
        groundVisible: this.ground.visible,
        groundAlpha: this.ground.alpha,
        groundScale: { x: this.ground.scaleX, y: this.ground.scaleY },
        groundActive: this.ground.active,
      });
      
      // 地面をワールド座標に変換する前に、カメラを適切な位置に移動
      // カメラをボールの位置に合わせるが、地面が画面内に収まるように調整する
      const groundLeft = groundX;
      const groundRight = groundX + groundWidth;
      
      const flyBallTimingResult = (this as any).flyBallTimingResult;
      console.log('[executeHitCut] カメラ位置調整前:', {
        flyBallTimingResult,
        groundLeft,
        groundRight,
        cameraX: currentCameraX,
        hitBallBodyExists: !!this.hitBallBody,
      });
      
      if (this.hitBallBody) {
        // ボールの位置にカメラを合わせる
        const ballX = this.hitBallBody.x;
        let newCameraX = ballX - cam.width / 2;
        
        // ただし、地面が画面内に収まるように調整
        // カメラの左端が地面の左端より左にある場合は、カメラを地面の左端に合わせる
        const cameraLeft = newCameraX;
        if (cameraLeft < groundLeft) {
          newCameraX = groundLeft;
          console.log('[executeHitCut] カメラを地面の左端に調整（ボール追従を維持）:', {
            ballX,
            originalCameraX: ballX - cam.width / 2,
            adjustedCameraX: newCameraX,
            groundLeft,
          });
        }
        
        cam.setScroll(newCameraX, currentCameraY);
        console.log('[executeHitCut] カメラをボールの位置に移動:', {
          ballX,
          newCameraX,
          oldCameraX: currentCameraX,
          groundLeft,
          groundRight,
        });
      } else {
        // ボールが存在しない場合は、カメラを地面の範囲内に調整
        const cameraLeft = currentCameraX;
        if (groundLeft < cameraLeft) {
          const newCameraX = groundLeft;
          cam.setScroll(newCameraX, currentCameraY);
          console.log('[executeHitCut] ボールなし: カメラを地面の左端に合わせる:', {
            oldCameraX: currentCameraX,
            newCameraX,
            groundLeft,
            groundRight,
          });
        }
      }
      
      // カメラ位置を更新した後、再度地面のワールド座標を計算
      const updatedCameraX = cam.scrollX;
      const updatedGroundWorldTL = cam.getWorldPoint(0, groundYPos);
      const updatedGroundY = updatedGroundWorldTL.y;
      
      // 地面をワールド座標に変換
      this.ground.setScrollFactor(1, 1);
      this.ground.setX(groundX);
      this.ground.setY(updatedGroundY);
      this.ground.setSize(groundWidth, groundHeight);
      // 画像の高さが2倍になったので、setTileScale(1, 1)で1段表示になる
      this.ground.setTileScale(1, 1);
      // 地面を明示的に表示状態にする
      this.ground.setVisible(true);
      this.ground.setAlpha(1.0);
      this.ground.setDepth(0);
      this.ground.setActive(true);
      
      console.log('[executeHitCut] 地面の設定後:', {
        x: this.ground.x,
        y: this.ground.y,
        width: this.ground.width,
        height: this.ground.height,
        visible: this.ground.visible,
        alpha: this.ground.alpha,
        depth: this.ground.depth,
        active: this.ground.active,
        cameraX: cam.scrollX,
        cameraY: cam.scrollY,
      });
      // 背景もワールド座標に移す（下辺を地面の上辺に合わせる）
      if (this.bg) {
        const bgOffsetY = 130; // 画面の上端から130ピクセル下に配置
        const bgWorldTL = cam.getWorldPoint(0, bgOffsetY); // 背景の上端のワールド座標
        const bgTexture = this.textures.get('bg');
        const bgImageHeight = bgTexture ? bgTexture.source[0].height : (groundYPos - bgOffsetY);
        this.bg.setScrollFactor(1, 1);
        this.bg.setX(groundCenterX - groundWidth / 2);
        this.bg.setY(bgWorldTL.y); // 背景の上端
        this.bg.setSize(groundWidth, bgImageHeight); // 画像の実サイズのまま
        // 横方向のみタイル、縦方向は実サイズのまま（スケール調整なし）
        this.bg.setTileScale(1, 1);
        // 背景画像のスケールを1.5倍に
        this.bg.setScale(1.5, 1.5);
      }

      // キャラクターと地面のワールド座標変換のみ（カメラ追従はupdate()で処理）
    };

    // VERY_EARLYの時は即座に実行、それ以外はタイマーで実行
    if (flyBallTimingResult === TimingResult.VERY_EARLY) {
      // VERY_EARLYの時は即座に実行（カメラ追従を開始するため）
      executeHitCut();
    } else {
      // それ以外はタイマーで実行
      this.hitCutTimer = this.time.delayedCall(hitCutMs, executeHitCut);
    }

    // 衝突イベントを設定（一度だけ設定するように、既存のリスナーを削除）
    if (!(this as any).collisionListenerAdded) {
      this.matter.world.on('collisionstart', (event: any) => {
        if (!this.hitBallBody) return;

        for (let i = 0; i < event.pairs.length; i++) {
          const bodyA = event.pairs[i].bodyA;
          const bodyB = event.pairs[i].bodyB;
          const ballBody = this.hitBallBody.body;

          // ボールとフェンスの衝突を検知（地面との衝突より先にチェック）
          if (
            ballBody &&
            this.fenceBody &&
            ((bodyA === ballBody && bodyB === this.fenceBody) ||
              (bodyA === this.fenceBody && bodyB === ballBody))
          ) {
            console.log('ボールとフェンスが衝突しました！', {
              ballX: this.hitBallBody.x,
              ballY: this.hitBallBody.y,
              fenceX: this.fenceBody.position.x,
              fenceY: this.fenceBody.position.y,
            });
            // フェンス衝突音を再生（1回のみ）
            if (!this.fenceHitSoundPlayed) {
              this.sound.play('fence', { volume: 0.5 });
              this.fenceHitSoundPlayed = true;
            }
            continue; // 次のペアをチェック
          }

          // ボールと地面の衝突を検知
          if (
            !this.ballLanded &&
            ballBody &&
            this.groundBody &&
            ((bodyA === ballBody && bodyB === this.groundBody) ||
              (bodyA === this.groundBody && bodyB === ballBody))
          ) {
            console.log('ボールと地面が衝突しました！', {
              ballX: this.hitBallBody.x,
              ballY: this.hitBallBody.y,
              groundX: this.groundBody.position.x,
              groundY: this.groundBody.position.y,
            });
            this.ballLanded = true;
            // 着地時に角速度（回転速度）を大幅に減らして転がりを抑える
            if (this.hitBallBody.body) {
              const body = this.hitBallBody.body as MatterJS.BodyType;
              if ((body as any).angularVelocity) {
                // 着地時に角速度を90%減らす（0.1倍に）
                (body as any).angularVelocity = (body as any).angularVelocity * 0.1;
              }
            }
            // 着地時の処理（必要に応じて）
            continue; // 次のペアをチェック
          }
        }
      });
      (this as any).collisionListenerAdded = true;
      console.log('衝突イベントリスナーを設定しました');
    }

    // 結果はボールが止まった時に保存される（実際の移動距離で判定）
    // ここではタイミングと角度のみ保存
    (this as any).flyBallTimingResult = timingResult;
    (this as any).flyBallAngle = angle;
  }

  private calculateDistance(timingResult: TimingResult, angle: number, isInBest: boolean): number {
    // タイミング係数（ベストが最大、ただしバレルゾーン外では抑える）
    let timingMultiplier = 1.0;
    if (timingResult === TimingResult.BEST) {
      if (isInBest) {
        timingMultiplier = 1.5; // ベスト + バレルゾーン内：最大
      } else {
        timingMultiplier = 0.95; // ベスト + バレルゾーン外：少しだけ抑える
      }
    } else if (timingResult === TimingResult.EARLY || timingResult === TimingResult.LATE) {
      timingMultiplier = 0.75; // 早い/遅いを少しだけ抑える
    } else {
      timingMultiplier = 0.55; // とても早い/とても遅いを少しだけ抑える
    }

    // 角度係数（30度に近いほど大きい）
    // ベスト角度は30度（バレルゾーン：25度〜35度、30度が最も飛びやすい）
    const bestBandCenter = 30; // ベスト角度（度）
    const bestBandHalf = this.config.bestBandWidth / 2;
    const angleDiff = Math.abs(angle - bestBandCenter);

    // 角度係数を計算（30度に近いほど大きい、加算モデル向けに調整）
    // 加算モデルでは angleBonus = baseDistance * (angleMultiplier - 1.0) なので
    // 1.0が基準（補正なし）、1.0以上がボーナス、1.0未満がペナルティ
    let angleMultiplier = 1.0;
    if (angleDiff <= bestBandHalf) {
      // ベスト帯内（25度から35度、バレルゾーン）
      const normalized = 1.0 - (angleDiff / bestBandHalf);
      if (angleDiff === 0) {
        // 30度の時は最大1.5倍（+50%ボーナス）
        angleMultiplier = 1.5;
      } else {
        // 25度/35度は1.0倍（補正なし）、30度に近づくほど1.5倍まで上昇
        angleMultiplier = 1.0 + (normalized * 0.5);
      }
    } else {
      // ベスト帯外：緩やかにペナルティ（最低0.85倍まで）
      // 35度超は少しずつ下がるが、急激には落ちない
      const outsideDiff = angleDiff - bestBandHalf;
      // 20度離れても0.85倍（-15%）程度に抑える
      angleMultiplier = Math.max(0.85, 1.0 - (outsideDiff * 0.0075));
    }

    // フルBest係数（タイミングベストかつ30度に非常に近い場合）
    let fullBestMultiplier = 1.0;
    const isBestTimingAnd30Degrees = timingResult === TimingResult.BEST && angleDiff <= bestBandHalf * 0.5;
    if (isBestTimingAnd30Degrees) {
      // タイミングBESTかつ30度±2.5度以内（27.5度〜32.5度）
      fullBestMultiplier = this.config.fullBestMultiplier;
    }

    // 距離計算（タイミングを基準値として固定し、角度は加点/減点で補正）
    const timingBase = this.config.baseDistance * timingMultiplier;
    const angleBonus = this.config.baseDistance * (angleMultiplier - 1.0);
    const fullBestBonus = this.config.baseDistance * (fullBestMultiplier - 1.0);

    let distance =
      (timingBase + angleBonus + fullBestBonus) *
        this.config.extraDistanceMultiplier +
      this.config.extraDistanceAdd;

    // BEST+30度以外の場合、相対的に飛距離を下げる
    if (!isBestTimingAnd30Degrees) {
      distance *= 1.0;
    }

    // 距離は0以上を保証（最大距離の制限は削除）
    return Math.max(0, distance);
  }

  // ストライク表示を更新
  private updateStrikeDisplay(): void {
    if (this.strikeText) {
      this.strikeText.setText(`ストライク: ${this.strikes}`);
      // 3ストライクの場合は赤色に変更
      if (this.strikes >= 3) {
        this.strikeText.setColor('#ff0000');
      } else {
        this.strikeText.setColor('#ffff00');
      }
    }
  }

  // ボールが停止している画面で結果を表示（画面切り替え前）
  private showResultOnBallStoppedScreen(pitchResult: PitchResult): void {
    console.log('[showResultOnBallStoppedScreen] 開始:', {
      pitchResult,
      currentPitch: this.currentPitch,
      totalPitches: this.config.totalPitches,
      totalScore: this.totalScore,
    });

    // カメラ追従をリセット
    this.waitFollowUntilCentered = false;
    this.followXOnly = false;
    this.prevBallScreenX = null;
    this.followStrength = 0;
    // 表示速度をリセット（次の打球用）
    (this as any).displaySpeedKmh = null;

    // 未発火の切り替えタイマーがあれば止める
    if (this.hitCutTimer) {
      this.hitCutTimer.remove(false);
      this.hitCutTimer = null;
    }

    // 角度バーは常に表示（非表示にしない）

    // デバッグテキストは常に表示（最終的な値を表示）
    const finalDisplaySpeed = (this as any).displaySpeedKmh?.toFixed(1) || '0.0';
    const finalDisplayDistance = (this as any).finalDisplayDistanceMx?.toFixed(1) || (this as any).displayDistanceMx?.toFixed(1) || '0.0';
    const finalDisplayAngle = (this as any).flyBallAngle ? Math.floor((this as any).flyBallAngle) : 0;
    this.debugText.setText(
            `打球速度: ${finalDisplaySpeed} km/h\n飛距離: ${finalDisplayDistance} m\n角度: ${finalDisplayAngle}°`
    );
    this.debugText.setVisible(true);

    // 結果テキストを表示（ボールが停止している画面で表示）
    let resultColor = '#ffffff';
    if (pitchResult.result === HitResult.HR) {
      resultColor = '#ffff00';
    } else if (pitchResult.result === HitResult.FOUL) {
      resultColor = '#ff8800';
    } else {
      resultColor = '#ffffff'; // Missの色を白に
    }

    // HRをHome Runに変換し、スコアも一緒に表示
    if (pitchResult.result === HitResult.HR) {
      this.resultText.setText('ホームラン').setColor(resultColor).setVisible(true);
      if (pitchResult.score > 0) {
        this.scoreGainText.setText(`+${pitchResult.score}`).setVisible(true);
      }
      // ホームラン表示時にparticle2.jsonを再生
      this.playParticleAnimation();
      // ホームラン表示時にover_score.mp3を再生
      this.sound.play('over_score', { volume: 0.5 });
    } else {
      // 結果を日本語に変換
      let resultText = '';
      if (pitchResult.result === HitResult.FOUL) {
        resultText = 'ファウル';
      } else if (pitchResult.result === HitResult.MISS) {
        resultText = 'ミス';
      } else {
        resultText = pitchResult.result; // フォールバック
      }
      this.resultText.setText(resultText).setColor(resultColor).setVisible(true);
    }

    // タイミング結果は既に表示されているので、ここでは表示しない
    // 角度の結果も既に表示されているので、ここでは表示しない

    // スコアを加算
    this.totalScore += pitchResult.score;

    // UI更新
    this.scoreText.setText(`スコア: ${this.totalScore}`);
    // 10球制限を撤廃したため、Balls表示は削除
    // const remaining = this.config.totalPitches - this.currentPitch;
    // this.pitchCountText.setText(`Balls: ${remaining}`);

    // 3ストライクでゲームオーバー
    if (this.strikes >= 3) {
      console.log('[showResultOnBallStoppedScreen] 3ストライク: ゲームオーバー');
      const delay = pitchResult.result === HitResult.HR ? 2000 : 1500;
      this.time.delayedCall(delay, () => {
        // パーティクルアニメーションを非表示
        this.stopParticleAnimation();
        // BGMを確実に停止
        try {
          const bgm = this.sound.get('bgm');
          if (bgm && bgm.isPlaying) {
            bgm.stop();
          }
          // すべてのBGMサウンドを停止（念のため）
          this.sound.stopAll();
        } catch (e) {
          console.warn('BGM停止エラー:', e);
        }
        // game_endを呼び出してからResult画面に遷移
        callGameEnd(this.totalScore, () => {
          this.scene.start('ResultScene', {
            totalScore: this.totalScore,
            pitchResults: this.pitchResults,
            totalPitches: this.currentPitch,
          });
        });
      });
      return;
    }

    // 一定時間後に画面を切り替えて次の打席へ
    const delay = pitchResult.result === HitResult.HR ? 2000 : 1500;
    console.log('[showResultOnBallStoppedScreen] 画面切り替えをスケジュール:', {
      delay,
      currentPitch: this.currentPitch,
      strikes: this.strikes,
    });
    this.time.delayedCall(delay, () => {
      console.log('[showResultOnBallStoppedScreen] 画面切り替え実行:', {
        currentPitch: this.currentPitch,
        totalPitches: this.config.totalPitches,
      });

      // 物理ボールを破棄
      if (this.hitBallBody) {
        this.hitBallBody.destroy();
        this.hitBallBody = null;
      }

      // 地面ボディも破棄（次の flyBall で再作成する）
      if (this.groundBody) {
        this.matter.world.remove(this.groundBody);
        this.groundBody = null;
      }

      // フェンスも破棄
      if (this.fenceBody) {
        this.matter.world.remove(this.fenceBody);
        this.fenceBody = null;
      }
      if (this.fenceVisual) {
        this.fenceVisual.destroy();
        this.fenceVisual = null;
      }
      // 打球をクリア
      if (this.hitBall) {
        this.hitBall.destroy();
        this.hitBall = null;
      }

      // カメラの追従を停止して元の位置に戻す
      const initialCameraX = Number.isFinite((this as any).flyBallInitialCameraX) ? (this as any).flyBallInitialCameraX : 0;
      this.tweens.killTweensOf(this.cameras.main);
      this.cameras.main.stopFollow();
      this.cameras.main.setScroll(initialCameraX, this.gameStartCamY);

      // 地面を画面固定に戻す
      const { width, height } = this.scale;
      const groundHeight = height * 0.3;
      const groundY = height - groundHeight;
      this.ground.setScrollFactor(0, 0);
      this.ground.setX(0);
      this.ground.setY(groundY);
      this.ground.setSize(width, groundHeight);
      // 地面を明示的に表示状態にする
      this.ground.setVisible(true);
      // 背景も画面固定に戻す（地面の上に配置）
      if (this.bg) {
        const bgOffsetY = 130; // 画面の上端から130ピクセル下に配置
        const bgTexture = this.textures.get('bg');
        const bgImageHeight = bgTexture ? bgTexture.source[0].height : (groundY - bgOffsetY);
        this.bg.setScrollFactor(0, 0);
        this.bg.setX(0);
        this.bg.setY(bgOffsetY); // 画面の上端より上に配置
        this.bg.setSize(width, bgImageHeight); // 画像の実サイズのまま
        this.bg.setOrigin(0, 0); // 左上を原点に
        // 横方向のみタイル、縦方向は実サイズのまま（スケール調整なし）
        this.bg.setTileScale(1, 1);
        // 背景画像のスケールを1.5倍に
        this.bg.setScale(1.5, 1.5);
      }

      // ピッチャー/バッター/キャッチャー/バットを「画面固定」に戻す（超重要）
      if (this.pitcher && this.pitcherScreen) {
        this.pitcher.setScrollFactor(0, 0);
        this.pitcher.setPosition(this.pitcherScreen.x, this.pitcherScreen.y);
        this.pitcher.setVisible(true);
      }
      if (this.batter && this.batterScreen) {
        this.batter.setScrollFactor(0, 0);
        this.batter.setPosition(this.batterScreen.x, this.batterScreen.y);
        this.batter.setVisible(true);
        // バッターのアニメーションをフレーム0にリセット
        if (this.batter.anims) {
          this.batter.anims.stop();
          this.batter.setFrame(0);
        }
      }
      if (this.catcher && this.catcherScreen) {
        this.catcher.setScrollFactor(0, 0);
        this.catcher.setPosition(this.catcherScreen.x, this.catcherScreen.y);
        this.catcher.setVisible(true);
      }
      if (this.bat && this.batScreen) {
        this.bat.setScrollFactor(0, 0);
        this.bat.setPosition(this.batScreen.x, this.batScreen.y);
        this.bat.setVisible(true);
      }

      // 10球制限を撤廃したため、常に次の投球へ（3ストライクの場合は既に処理済み）
      {
        // 次の投球（画面が切り替わる前にリセット）
        // 打球速度、飛距離、角度を0にリセット
        (this as any).displaySpeedKmh = null;
        (this as any).displayDistanceMx = null;
        (this as any).finalDisplayDistanceMx = null;
        (this as any).flyBallAngle = null;
        (this as any).flyBallStartX = null;
        (this as any).flyBallStartY = null;
        this.debugText.setText(
          `打球速度: 0 km/h\n飛距離: 0 m\n角度: 0°`
        );
        // 結果テキストを非表示（画面切り替え前に）
        this.resultText.setVisible(false);
        this.distanceText.setVisible(false);
        this.scoreGainText.setVisible(false);
        // パーティクルアニメーションも非表示（画面切り替え前に）
        this.stopParticleAnimation();
        // 角度メーターの針をリセット（画面切り替え前に）
        if (this.angleMeterPointer) {
          this.angleMeterPointer.setTo(
            this.angleMeterOriginX, this.angleMeterOriginY, // 原点
            this.angleMeterOriginX, this.angleMeterOriginY  // 初期位置（原点と同じ）
          );
          this.angleMeterPointer.setVisible(false);
        }
        console.log('[showResultOnBallStoppedScreen] 次の投球を開始');
        this.startNextPitch();
      }
    });
  }

  private showResult(pitchResult: PitchResult): void {
    console.log('[showResult] 開始:', {
      pitchResult,
      currentPitch: this.currentPitch,
      totalPitches: this.config.totalPitches,
      totalScore: this.totalScore,
    });

    // カメラ追従をリセット
    this.waitFollowUntilCentered = false;
    this.followXOnly = false;
    this.prevBallScreenX = null;
    this.followStrength = 0;
    // 表示速度をリセット（次の打球用）
    (this as any).displaySpeedKmh = null;

    // 未発火の切り替えタイマーがあれば止める
    if (this.hitCutTimer) {
      this.hitCutTimer.remove(false);
      this.hitCutTimer = null;
    }

    // 物理ボールを破棄
    if (this.hitBallBody) {
      this.hitBallBody.destroy();
      this.hitBallBody = null;
    }

    // 地面ボディも破棄（次の flyBall で再作成する）
    if (this.groundBody) {
      this.matter.world.remove(this.groundBody);
      this.groundBody = null;
    }

    // フェンスも破棄
    if (this.fenceBody) {
      this.matter.world.remove(this.fenceBody);
      this.fenceBody = null;
    }
    if (this.fenceVisual) {
      this.fenceVisual.destroy();
      this.fenceVisual = null;
    }
    // 打球をクリア
    if (this.hitBall) {
      this.hitBall.destroy();
      this.hitBall = null;
    }

    // 角度バーは常に表示（非表示にしない）

    // デバッグテキストは常に表示（最終的な値を表示）
    const finalDisplaySpeed = (this as any).displaySpeedKmh?.toFixed(1) || '0.0';
    const finalDisplayDistance = (this as any).finalDisplayDistanceMx?.toFixed(1) || (this as any).displayDistanceMx?.toFixed(1) || '0.0';
    const finalDisplayAngle = (this as any).flyBallAngle ? Math.floor((this as any).flyBallAngle) : 0;
    this.debugText.setText(
            `打球速度: ${finalDisplaySpeed} km/h\n飛距離: ${finalDisplayDistance} m\n角度: ${finalDisplayAngle}°`
    );
    this.debugText.setVisible(true);

    // 結果テキストを表示
    let resultColor = '#ffffff';
    if (pitchResult.result === HitResult.HR) {
      resultColor = '#ffff00';
    } else if (pitchResult.result === HitResult.FOUL) {
      resultColor = '#ff8800';
    } else {
      resultColor = '#ffffff'; // Missの色を白に
    }

    // HRをHome Runに変換し、スコアも一緒に表示
    if (pitchResult.result === HitResult.HR) {
      this.resultText.setText('ホームラン').setColor(resultColor).setVisible(true);
      if (pitchResult.score > 0) {
        this.scoreGainText.setText(`+${pitchResult.score}`).setVisible(true);
      }
      // ホームラン表示時にparticle2.jsonを再生
      this.playParticleAnimation();
      // ホームラン表示時にover_score.mp3を再生
      this.sound.play('over_score', { volume: 0.5 });
    } else {
      // 結果を日本語に変換
      let resultText = '';
      if (pitchResult.result === HitResult.FOUL) {
        resultText = 'ファウル';
      } else if (pitchResult.result === HitResult.MISS) {
        resultText = 'ミス';
      } else {
        resultText = pitchResult.result; // フォールバック
      }
      this.resultText.setText(resultText).setColor(resultColor).setVisible(true);
    }

    // タイミング結果は既に表示されているので、ここでは表示しない
    // 角度の結果も既に表示されているので、ここでは表示しない

    // スコアを加算
    this.totalScore += pitchResult.score;

    // UI更新
    this.scoreText.setText(`スコア: ${this.totalScore}`);
    // 10球制限を撤廃したため、Balls表示は削除
    // const remaining = this.config.totalPitches - this.currentPitch;
    // this.pitchCountText.setText(`Balls: ${remaining}`);

    // 3ストライクでゲームオーバー
    if (this.strikes >= 3) {
      console.log('[showResult] 3ストライク: ゲームオーバー');
      const delay = pitchResult.result === HitResult.HR ? 2000 : 1500;
      this.time.delayedCall(delay, () => {
        // パーティクルアニメーションを非表示
        this.stopParticleAnimation();
        // BGMを確実に停止
        try {
          const bgm = this.sound.get('bgm');
          if (bgm && bgm.isPlaying) {
            bgm.stop();
          }
          // すべてのBGMサウンドを停止（念のため）
          this.sound.stopAll();
        } catch (e) {
          console.warn('BGM停止エラー:', e);
        }
        // game_endを呼び出してからResult画面に遷移
        callGameEnd(this.totalScore, () => {
          this.scene.start('ResultScene', {
            totalScore: this.totalScore,
            pitchResults: this.pitchResults,
            totalPitches: this.currentPitch,
          });
        });
      });
      return;
    }

    // 次の投球へ
    const delay = pitchResult.result === HitResult.HR ? 2000 : 1500;
    console.log('[showResult] 次の打席への遷移をスケジュール:', {
      delay,
      currentPitch: this.currentPitch,
      strikes: this.strikes,
    });
    this.time.delayedCall(delay, () => {
      console.log('[showResult] 遅延コールバック実行:', {
        currentPitch: this.currentPitch,
        strikes: this.strikes,
      });
      // 10球制限を撤廃したため、常に次の投球へ（3ストライクの場合は既に処理済み）
      {
        // 次の投球（画面が切り替わる前にリセット）
        // 打球速度、飛距離、角度を0にリセット
        (this as any).displaySpeedKmh = null;
        (this as any).displayDistanceMx = null;
        (this as any).finalDisplayDistanceMx = null;
        (this as any).flyBallAngle = null;
        (this as any).flyBallStartX = null;
        (this as any).flyBallStartY = null;
        this.debugText.setText(
          `打球速度: 0 km/h\n飛距離: 0 m\n角度: 0°`
        );
        // 結果テキストを非表示（画面切り替え前に）
        this.resultText.setVisible(false);
        this.distanceText.setVisible(false);
        this.scoreGainText.setVisible(false);
        // パーティクルアニメーションも非表示（画面切り替え前に）
        this.stopParticleAnimation();
        // 角度メーターの針をリセット（画面切り替え前に）
        if (this.angleMeterPointer) {
          this.angleMeterPointer.setTo(
            this.angleMeterOriginX, this.angleMeterOriginY, // 原点
            this.angleMeterOriginX, this.angleMeterOriginY  // 初期位置（原点と同じ）
          );
          this.angleMeterPointer.setVisible(false);
        }
        console.log('[showResult] 次の投球を開始');
        this.startNextPitch();
      }
    });
  }

  private playParticleAnimation(): void {
    // 既存のアニメーションがあれば破棄
    this.stopParticleAnimation();

    // lottie-webが利用可能か確認
    if (!window.lottie || !window.lottie.loadAnimation) {
      console.warn('Lottieライブラリが利用できません');
      return;
    }

    try {
      // resultTextの位置を取得（ホームランテキストの位置）
      const { width, height } = this.scale;
      const resultTextX = width / 2; // resultTextのX座標（画面中央）
      const resultTextY = height * 0.3; // resultTextのY座標（画面の30%）

      // パーティクルアニメーション用のコンテナを作成
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.width = '500px';
      container.style.height = '500px';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '10001';
      
      // canvasの位置とサイズを取得して、resultTextの位置をDOM座標に変換
      const canvas = this.game.canvas;
      const canvasRect = canvas.getBoundingClientRect();
      const gameContainer = document.getElementById('game-container');
      if (gameContainer && canvas) {
        // Phaserの座標をDOM座標に変換
        // canvasは中央配置されているので、canvasの左上からの相対位置を計算
        const scaleX = canvasRect.width / width;
        const scaleY = canvasRect.height / height;
        const domX = canvasRect.left + (resultTextX * scaleX);
        const domY = canvasRect.top + (resultTextY * scaleY);
        
        container.style.left = `${domX}px`;
        container.style.top = `${domY}px`;
        container.style.transform = 'translate(-50%, -50%)'; // 中央揃え
        
        gameContainer.appendChild(container);
        this.particleContainer = container; // コンテナへの参照を保存

        // アセットパスを取得
        const pathname = window.location.pathname;
        const basePath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname.split('/').slice(0, -1).join('/') || '';
        const assetsPath = basePath ? `${basePath}/assets` : '/assets';
        const particlePath = `${assetsPath}/animations/particle2.json`;

        // Lottieアニメーションを読み込んで再生
        this.particleAnimation = window.lottie.loadAnimation({
          container: container,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          path: particlePath,
        });

        // アニメーション終了時にコンテナを削除
        this.particleAnimation.addEventListener('complete', () => {
          if (container && container.parentNode) {
            container.parentNode.removeChild(container);
          }
          this.particleAnimation = null;
          this.particleContainer = null;
        });
      }
    } catch (e) {
      console.error('パーティクルアニメーションの再生に失敗しました:', e);
      this.particleAnimation = null;
      this.particleContainer = null;
    }
  }

  private stopParticleAnimation(): void {
    // 既存のアニメーションがあれば破棄
    if (this.particleAnimation) {
      try {
        this.particleAnimation.destroy();
      } catch (e) {
        // エラーが発生した場合は無視
      }
      this.particleAnimation = null;
    }
    // コンテナを削除
    if (this.particleContainer && this.particleContainer.parentNode) {
      this.particleContainer.parentNode.removeChild(this.particleContainer);
      this.particleContainer = null;
    }
  }
}
